import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AltmanZScore,
  AuctionListing,
  Company,
  CrzContract,
  FinancialSummaryYear,
  GraphEdge,
  GraphNode,
  InsolvencyInfo,
  ListingRow,
  NetworkGraph as NetworkGraphData,
  Person,
  RatioCategory,
  RatioRow,
  RiskScore,
  SectorBenchmark,
  TrendPoint,
} from '@marstein-radar/shared';
import { computeFinancialRatios } from '@marstein-radar/shared';
import { NetworkGraph } from '../features/network/NetworkGraph';
import { Sparkline } from '../features/financials/Sparkline';
import { TrendChart } from '../features/financials/TrendChart';
import {
  riskBand,
  riskWord,
  signalSeverity,
  delta,
  formatEur,
  formatEurCompact,
  formatPct,
  formatRatio,
  formatDays,
  personResultStyle,
  buttonStyle,
} from '../styles';
import { useAuth } from '../auth/AuthContext';

// 2026-07-20 — Exekúcie (len firemná podmnožina), lokálny typ (nie v shared,
// rovnaký dôvod ako RPO surové polia nižšie — malá interná API projekcia).
interface ExecutionNotice {
  id: string;
  caseNumber: string | null;
  proposalMethod: string | null;
  executorName: string | null;
  debtorName: string | null;
  debtorIco: string;
  publishedAt: string | null;
}

// 2026-07-20 — Sankčná kontrola (EU FSF), lokálny typ.
interface SanctionMatch {
  matchedName: string;
  sanctionedName: string;
  subjectType: string;
  programme: string | null;
}

// 2026-07-25 — Priama rola osoba↔firma z `/persons` (jeden SELECT, žiadny
// graf), lokálny typ zrkadliaci `RoleWithPerson` z API (persons.repository.ts).
interface CompanyPersonRole {
  role: {
    roleLabel: string;
    roleCategory: string;
    validFrom: string | null;
    validTo: string | null;
    source: string;
  };
  person: {
    id: string;
    fullName: string;
    birthDate: string | null;
  };
}

// 2026-07-25 — /companies/:ico/ownership (ownership.repository.ts), najnovší
// zápis z ov.or_podanie_issues. `shareholders[].name` sa páruje na RPO
// `stakeholders[].personName.formatedName` cez normalizePersonNameLoose —
// obe strany môžu mať/nemať titul, preto nie presné porovnanie reťazcov.
interface OwnershipShareholder {
  name: string;
  isCompany: boolean;
  address: string | null;
  vkladAmount: number | null;
  splatenieAmount: number | null;
  sharePct: number | null;
}
interface OwnershipSnapshot {
  filedAt: string | null;
  totalEquity: string | null;
  currency: string | null;
  shareholders: OwnershipShareholder[];
}

interface RoleWithCompany {
  role: {
    id: string;
    roleLabel: string;
    roleCategory: string;
    validFrom: string | null;
    validTo: string | null;
  };
  company: Company;
}

// RPO surové polia (registryData.raw) — appka doteraz extrahovala len "aktuálny"
// záznam (currentOrLatest() na backende), história ostávala nepoužitá v UI. Typy
// tu lokálne (nie v shared), pretože `raw` je zámerne netypované na backende
// (viď legal-form.parser.ts) — polia overené priamou DB inšpekciou 2026-07-17.
interface RpoActivity {
  economicActivityDescription?: string;
  validFrom?: string;
  validTo?: string;
}
interface RpoNameEntry {
  value?: string;
  validFrom?: string;
  validTo?: string;
}
interface RpoAddressEntry {
  street?: string;
  buildingNumber?: string;
  municipality?: { value?: string };
  validFrom?: string;
  validTo?: string;
}
// Plný riadkový výpis súvahy/výsledovky (Týž. 21) — zrkadlí
// DecodedStatementRow/DecodedStatementYear z apps/api/.../financial-summary.service.ts.
interface DecodedStatementRow {
  oznacenie: string | null;
  text: string;
  current: number | null;
  previous: number | null;
}
interface DecodedStatementYear {
  period: number;
  aktiva: DecodedStatementRow[] | null;
  pasiva: DecodedStatementRow[] | null;
  vykazZiskovAStrat: DecodedStatementRow[] | null;
}
// Spoločníci — RPO `stakeholders` (NIE percentuálny podiel/výška vkladu, tie RPO
// neposkytuje, len Obchodný register, ku ktorému nemáme štruktúrovaný prístup —
// viď plán "rustling-launching-hippo"). Meno + adresa + odkedy je spoločníkom.
interface RpoStakeholderEntry {
  personName?: { formatedName?: string };
  stakeholderType?: { value?: string };
  address?: { municipality?: { value?: string }; country?: { code?: string; value?: string } };
  validFrom?: string;
  validTo?: string;
}
// Company type v @marstein-radar/shared je zámerne užší než skutočný API výstup
// (backend posiela celý CompanyRow z Drizzle, nie len zod schému) — lokálne
// rozšírenie namiesto úpravy shared typu, rovnaký pragmatizmus ako inde v appke.
interface CompanyDetail extends Company {
  legalFormName?: string | null;
  terminatedAt?: string | null;
  hasRpvsRegistration?: boolean | null;
  hasForeignStakeholder?: boolean | null;
  // Finančná správa SR Open Data (2026-07-17, opendata-bulk-sync) — voľne dostupné
  // (CC 4.0 / CC0), viď firmyradar_data_source_survey memory.
  hasDphRegistration?: boolean | null;
  dphRegistrationType?: string | null;
  dphRegisteredFrom?: string | null;
  hasDphCancellationReason?: boolean | null;
  dphCancellationYear?: number | null;
  taxReliabilityIndex?: string | null;
  registryData?: {
    raw?: {
      activities?: RpoActivity[];
      fullNames?: RpoNameEntry[];
      addresses?: RpoAddressEntry[];
      stakeholders?: RpoStakeholderEntry[];
      // Konatelia/štatutárny orgán — na rozdiel od `stakeholders` (spoločníci)
      // toto RPO vracia bez samostatnej dozornej rady, tá je len v `/persons`
      // (2026-07-25, viď directPeople nižšie).
      statutoryBodies?: RpoStakeholderEntry[];
    };
  } | null;
}

function formatSkDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('sk-SK');
}

function addressLabel(a: RpoAddressEntry): string {
  return [[a.street, a.buildingNumber].filter(Boolean).join(' '), a.municipality?.value]
    .filter(Boolean)
    .join(', ');
}

// 2026-07-20 — Exekúcie: `proposalMethod` je surová hodnota z OV eForm
// (Other.ProposalMethod ItemCode/text, viď execution-xml.parser.ts). Zastavenie
// je opačný signál než začatie (exekúcia skončila), preto samostatné farebné
// pásmo namiesto jednotného "riziko" pillu pre všetky stavy.
function executionIsStopped(proposalMethod: string | null): boolean {
  return proposalMethod != null && /zastaven/i.test(proposalMethod);
}
function executionMethodLabel(proposalMethod: string | null): string {
  if (!proposalMethod) return 'Neznámy stav';
  if (/zastaven/i.test(proposalMethod)) return 'Zastavená';
  if (/zacati|začatí|zacat/i.test(proposalMethod)) return 'Začatá';
  if (/vyhlasenia_o_majetku/i.test(proposalMethod)) return 'Výzva na vyhlásenie o majetku';
  if (/sucinnosti/i.test(proposalMethod)) return 'Žiadosť o súčinnosť';
  return proposalMethod;
}

// Revízia 2026-07-20 — taby ZRUŠENÉ, stránka je jeden súvislý dokument s lepivou
// bočnou lištou (viď artefakt "Detail firmy — nové rozloženie"). Dôvod: v3 pravidlo
// znie "maximálny detail viditeľný rovno na obrazovke, nič za rozklikom" a päť
// exkluzívnych tabov bolo jeho presným opakom — navyše tab "Analýza" duplikoval
// obsah zvyšných štyroch. Vedľajší efekt: @media print už nemusí force-ovať
// `.tab-panel { display: block }`, lebo všetok obsah je v DOM aj vizuálne.
//
// Sekcie sú zoradené podľa toho, ako rýchlo zmenia obchodné rozhodnutie o firme —
// nie podľa zdroja dát a nie chronologicky podľa toho, kedy pribudli. Nová funkcia
// sa zaraďuje podľa váhy. Sekcia bez dát sa nevykresľuje vôbec (žiadne "žiadne
// záznamy" panely), preto sa zoznam pre lištu skladá až za behu z toho, čo reálne
// prišlo z API.
interface DocSection {
  /** Segment URL (`/firma/:ico/<key>`), '' pre Úvod (bez segmentu). */
  key: string;
  label: string;
  /** Stavová bodka v lište — nech je problémová sekcia vidieť bez toho, aby tam človek musel prekliknúť. */
  status?: 'ok' | 'bad';
}

// Kompletný zoznam platných segmentov URL (viď `sections.push(...)` nižšie) —
// nie každá firma má každú sekciu vyplnenú dátami, ale toto je zoznam sekcií,
// ktoré appka vôbec POZNÁ. Segment mimo tohto zoznamu je vždy chyba v URL.
const KNOWN_COMPANY_SECTIONS = new Set([
  'uctovna-zavierka',
  'odvetvovy-test',
  'zmluvy',
  'majetok',
  'zaklad',
  'historia',
]);

// Vlastníci a Prepojenia zlúčené do Základných údajov (2026-07-25, user:
// pôsobili ako duplicitné taby) — staré odkazy nech skončia rovno na
// obsahu, nie na prázdnej domovskej stránke sekcie.
const LEGACY_SECTION_REDIRECTS: Record<string, string> = {
  vlastnici: 'zaklad',
  prepojenia: 'zaklad',
};

// Profil firmy — Blok 3-4. IČO príde z URL (/firma/:ico), nie z lokálneho state, aby
// mala každá firma vlastnú SEO-friendly adresu (Týž. 13).
export function CompanyPage() {
  const { ico, section: rawSection } = useParams<{ ico: string; section?: string }>();
  // Skutočné podstránky (2026-07-24, user feedback + Finstat referencia) —
  // predtým jeden dlhý dokument s kotvami, teraz `/firma/:ico/<section>` mení
  // URL a zobrazí LEN jednu sekciu na celú šírku, zvyšok sa nevykreslí (nie je
  // v DOM). "Úvod" (bez segmentu v URL) = Zistenia+Rizikové skóre+Záväzky
  // voči štátu spolu, presne toľko, koľko malo ísť do bočného panelu v
  // artefakte, len appka má na to viac reálneho obsahu než mockup.
  const activeSection = rawSection ?? '';
  // Skok medzi podstránkami je navigácia, nie scroll v tom istom dokumente —
  // bez tohto by prehliadač po prekliknutí zostal scrollnutý tam, kde bol.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [ico, activeSection]);
  const navigate = useNavigate();
  // Neznámy segment v URL (preklep, starý/zdieľaný link na sekciu, ktorá
  // nikdy neexistovala) predtým vykreslil len hlavičku a pätičku bez obsahu
  // a bez vysvetlenia (audit zistenie 2026-07-24) — na rozdiel od známej
  // sekcie bez dát pre TÚTO firmu (rieši `EmptySection` nižšie), toto je
  // vždy chyba v URL, nie stav firmy, takže sa dá presmerovať okamžite bez
  // čakania na dáta.
  useEffect(() => {
    if (rawSection && LEGACY_SECTION_REDIRECTS[rawSection]) {
      navigate(`/firma/${ico}/${LEGACY_SECTION_REDIRECTS[rawSection]}`, { replace: true });
    } else if (rawSection && !KNOWN_COMPANY_SECTIONS.has(rawSection)) {
      navigate(`/firma/${ico}`, { replace: true });
    }
  }, [ico, rawSection, navigate]);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showHistorical, setShowHistorical] = useState(false);
  const [depth, setDepth] = useState(2);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [watchError, setWatchError] = useState<string | null>(null);
  const [watchLoading, setWatchLoading] = useState(false);

  useEffect(() => {
    setSelectedPerson(null);
    setWatchError(null);
  }, [ico]);

  const { data: watchlist } = useQuery<{ entry: unknown; company: Company }[]>({
    queryKey: ['watchlist'],
    queryFn: async () => {
      const res = await fetch('/api/v1/watchlist');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!user,
  });
  const isWatching = !!watchlist?.some((w) => w.company.ico === ico);

  async function toggleWatch(): Promise<void> {
    if (!user) {
      navigate('/prihlasenie');
      return;
    }
    setWatchError(null);
    setWatchLoading(true);
    try {
      const res = await fetch(`/api/v1/companies/${ico}/watch`, {
        method: isWatching ? 'DELETE' : 'POST',
        headers: { 'content-type': 'application/json' },
        body: isWatching ? undefined : '{}',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `${res.status} ${res.statusText}`);
      }
      await queryClient.invalidateQueries({ queryKey: ['watchlist'] });
    } catch (err) {
      setWatchError((err as Error).message);
    } finally {
      setWatchLoading(false);
    }
  }

  const { data, error, isLoading } = useQuery<CompanyDetail>({
    queryKey: ['company', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  useEffect(() => {
    if (data) document.title = `${data.name ?? data.ico} — Corpuls`;
  }, [data]);

  const { data: financialSummary, isLoading: financialSummaryLoading } = useQuery<
    FinancialSummaryYear[]
  >({
    queryKey: ['financial-summary', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/financial-summary`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  // Plný riadkový výpis súvahy/výsledovky — na stránke "Účtovná závierka" je
  // rovno vypísaný celý (žiadny "zobraziť viac", viď FinStat referencia), preto
  // sa doťahuje priamo pri návšteve tejto sekcie.
  const { data: fullStatements, isFetching: fullStatementsLoading } = useQuery<
    DecodedStatementYear[]
  >({
    queryKey: ['financial-statements-full', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/financial-statements-full`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico && activeSection === 'uctovna-zavierka',
  });

  const { data: trend, isLoading: trendLoading } = useQuery<TrendPoint[]>({
    queryKey: ['financial-trend', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/financial-trend`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  const { data: benchmark, isLoading: benchmarkLoading } = useQuery<SectorBenchmark | null>({
    queryKey: ['sector-benchmark', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/sector-benchmark`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      // Backend posiela prázdne telo (nie JSON "null") keď firma nemá NACE kód
      // — NestJS takto serializuje null/undefined return hodnoty. res.json() by
      // na prázdnom tele vyhodilo parse chybu (rovnaký bug ako pri ownership).
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    enabled: !!ico,
  });

  const { data: insolvency, isLoading: insolvencyLoading } = useQuery<InsolvencyInfo>({
    queryKey: ['insolvency', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/insolvency`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  const { data: crzContracts, isLoading: crzContractsLoading } = useQuery<CrzContract[]>({
    queryKey: ['crz-contracts', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/crz-contracts`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  const { data: companyAuctions, isLoading: companyAuctionsLoading } = useQuery<
    AuctionListing[]
  >({
    queryKey: ['company-auctions', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/auctions`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  // 2026-07-20 — Exekúcie (len firemná podmnožina, viď execution-notices schema).
  const { data: companyExecutions, isLoading: companyExecutionsLoading } = useQuery<
    ExecutionNotice[]
  >({
    queryKey: ['company-executions', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/executions`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  const { data: riskScore, isLoading: riskScoreLoading } = useQuery<RiskScore>({
    queryKey: ['risk-score', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/risk-score`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  // 2026-07-20 (audit-artefakt "stredný rozsah") — sankčná kontrola (EU FSF),
  // len informačná, mimo rizikového skóre (viď CompaniesService komentár).
  const { data: sanctionMatches, isLoading: sanctionMatchesLoading } = useQuery<SanctionMatch[]>({
    queryKey: ['sanctions', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/sanctions`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  const { data: network, isFetching: networkLoading } = useQuery<NetworkGraphData>({
    queryKey: ['network', ico, depth],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/network?depth=${depth}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  // Priame role tejto firmy (štatutári/orgány/spoločníci) — ľahší náprotivok
  // `network` (jeden SELECT namiesto depth-BFS grafu), pre "Vlastníci a
  // štatutári", ktorí nepotrebujú multi-hop prepojenia (2026-07-25).
  const { data: companyPersons, isLoading: companyPersonsLoading } = useQuery<
    CompanyPersonRole[]
  >({
    queryKey: ['company-persons', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/persons`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  // 2026-07-25 — % podielu spoločníkov (Slovensko.Digital ov.or_podanie_issues,
  // viď ownership.repository.ts na API strane). RPO stakeholders % neposkytuje
  // vôbec — toto dopĺňa tabuľku "Spoločníci" nižšie, nie je to nová sekcia
  // (user feedback z 2026-07-25: vyhýbať sa duplicitným tabuľkám pre to isté).
  const { data: ownership, isLoading: ownershipLoading } = useQuery<OwnershipSnapshot | null>({
    queryKey: ['ownership', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/ownership`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      // Backend posiela prázdne telo (nie JSON "null") keď firma nemá žiadny
      // záznam o vlastníctve — NestJS takto serializuje null/undefined return
      // hodnoty. res.json() by na prázdnom tele vyhodilo parse chybu.
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    enabled: !!ico,
  });

  // Týž. 18 — "podobné firmy" (rovnaké mesto), odporúčacia sekcia.
  const { data: similar, isLoading: similarLoading } = useQuery<Company[]>({
    queryKey: ['similar', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/similar`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  // 2026-07-20 (foaf.sk prieskum) — "iné firmy na tejto adrese", shell-company
  // red flag sekcia.
  const { data: sameAddress, isLoading: sameAddressLoading } = useQuery<
    { ico: string; name: string | null }[]
  >({
    queryKey: ['same-address', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/companies/${ico}/same-address`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  // Fáza 2 inzertnej sekcie (2026-07-15) — nekritické surfacing, prázdne pole je
  // legitímny (bežný) stav, žiadny error handling navyše.
  const { data: companyListings, isLoading: companyListingsLoading } = useQuery<ListingRow[]>({
    queryKey: ['company-listings', ico],
    queryFn: async () => {
      const res = await fetch(`/api/v1/listings/for-company/${ico}`);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json();
    },
    enabled: !!ico,
  });

  const { data: personCompanies, isFetching: personCompaniesLoading } = useQuery<RoleWithCompany[]>(
    {
      queryKey: ['person-companies', selectedPerson?.id],
      queryFn: async () => {
        const res = await fetch(`/api/v1/persons/${selectedPerson!.id}/companies`);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
      },
      enabled: !!selectedPerson,
    },
  );

  // Skeleton čaká na VŠETKY sekcie okrem grafu Prepojení (ten je jediný, kde je
  // pomalšie dotiahnutie akceptované — depth-BFS je podstatne drahšie než
  // ostatné jednoduché SELECTy). Predtým čakal len na hlavný `data` fetch a
  // každá sekcia sa dopĺňala zvlášť, ako jej fetch dobehol — pri pomalšom
  // pripojení to vyzeralo, akoby stránka "dopĺňala" dáta postupne (2026-07-26,
  // user feedback: chce všetko hneď, nie postupne).
  const contentLoading =
    isLoading ||
    financialSummaryLoading ||
    benchmarkLoading ||
    insolvencyLoading ||
    crzContractsLoading ||
    companyAuctionsLoading ||
    companyExecutionsLoading ||
    riskScoreLoading ||
    sanctionMatchesLoading ||
    companyPersonsLoading ||
    ownershipLoading ||
    similarLoading ||
    sameAddressLoading ||
    companyListingsLoading ||
    trendLoading;

  const narrative = buildNarrative(financialSummary);
  const hasDisqualified = !!riskScore?.signals.some((s) => s.type === 'disqualified_person');

  // --- Derivované hodnoty pre dokument (2026-07-20) ---------------------------
  // Boli pôvodne vnútri OverviewTab; ten je zrušený a jeho sekcie sa rozpustili do
  // hlavného dokumentu, takže výpočty musia byť tu (potrebuje ich aj hlavička aj
  // lišta, nielen jedna sekcia).
  const latest = financialSummary?.[0];
  const previousYear = financialSummary?.[1];
  // Kľúčové čísla — pôvodne v lepivej lište, tá teraz nesie len taby (2026-07-25,
  // user chcel taby úplne navrch, ale čísla majú ostať pri dokumente, nad
  // porovnaním s odvetvím, kam patria obsahovo).
  const railFigures: RailFigures | null = latest
    ? {
        revenue: { value: formatEurCompact(latest.revenue), delta: delta(latest.revenue, previousYear?.revenue) },
        netIncome: {
          value: formatEurCompact(latest.netIncome),
          delta: delta(latest.netIncome, previousYear?.netIncome),
          negative: (latest.netIncome ?? 0) < 0,
        },
        equity: {
          value: formatEurCompact(latest.equity),
          delta: delta(latest.equity, previousYear?.equity),
          negative: (latest.equity ?? 0) < 0,
        },
        liabilities: {
          value: formatEurCompact(latest.liabilities),
          delta: delta(latest.liabilities, previousYear?.liabilities),
          invertDelta: true,
        },
        debtRatio: { value: ratioPct(latest.liabilities, latest.totalAssets) },
        altmanZ: {
          value: riskScore?.altmanZScore ? String(riskScore.altmanZScore.score) : '—',
          tone: riskScore?.altmanZScore
            ? altmanBand(riskScore.altmanZScore.zone) === 'low'
              ? 'good'
              : altmanBand(riskScore.altmanZScore.zone) === 'high'
                ? 'bad'
                : undefined
            : undefined,
        },
      }
    : null;
  const spDebt = insolvency ? sumAmounts(insolvency.socpoistDebts) : null;
  const vszpDebt = insolvency ? sumAmounts(insolvency.vszpDebts) : null;
  const insolvencyEvents = insolvency?.events.filter((e) => e.eventType !== 'znizenie_imania');
  const proceedingCount =
    (insolvencyEvents?.length ?? 0) + (insolvency?.replikProceedings.length ?? 0);

  const raw = data?.registryData?.raw;

  // Osoby naviazané priamo na túto firmu, aktuálne väzby prvé. Predtým sa toto
  // odvodzovalo z hrán `network` grafu (depth-2 BFS cez firmy aj osoby), čo bolo
  // zbytočne pomalé pre zoznam, ktorý potrebuje len priame role tejto firmy —
  // 2026-07-25 (user feedback) prepojené na ľahší `/persons` endpoint (jeden
  // priamy SELECT, žiadny graf) namiesto čakania na celý network fetch.
  //
  // Kým `/persons` dobehne, ukáž aspoň konateľov z `raw.statutoryBodies` — tí
  // sú súčasťou hlavného company payloadu, teda dostupní okamžite, rovnako
  // ako "Spoločníci" nižšie (rovnaká otázka usera: "prečo Spoločníci hneď a
  // toto nie"). Chýba im dozorná rada a RPVS UBO (tie má len `/persons`), no
  // zoznam sa hneď po dobehnutí fetchu doplní na plný.
  const directPeople = (
    companyPersons
      ? companyPersons.map((r) => ({
          edge: {
            from: 'company',
            to: `person:${r.person.id}`,
            roleLabel: r.role.roleLabel,
            roleCategory: r.role.roleCategory,
            validFrom: r.role.validFrom,
            validTo: r.role.validTo,
            source: r.role.source,
          } satisfies GraphEdge,
          person: {
            id: r.person.id,
            type: 'person' as const,
            label: r.person.fullName,
            identityConfirmed: r.person.birthDate != null,
          } satisfies GraphNode,
        }))
      : (raw?.statutoryBodies ?? [])
          .filter((s) => s.personName?.formatedName)
          .map((s) => ({
            edge: {
              from: 'company',
              to: `rpo:${s.personName!.formatedName}`,
              roleLabel: s.stakeholderType?.value ?? 'Neuvedená rola',
              roleCategory: 'organ',
              validFrom: s.validFrom ?? null,
              validTo: s.validTo ?? null,
              source: 'rpo',
            } satisfies GraphEdge,
            person: {
              id: `rpo:${s.personName!.formatedName}`,
              type: 'person' as const,
              label: s.personName!.formatedName!,
              identityConfirmed: false,
            } satisfies GraphNode,
          }))
  ).sort((a, b) => Number(!!a.edge.validTo) - Number(!!b.edge.validTo));

  const hasRegistryHistory = registryHistoryHasContent(data);

  // Rok vzniku — Company typ ho nenesie (viď packages/shared/company.ts), ale RPO
  // raw áno: najstarší zápis názvu je prakticky dátum vzniku. Rovnaká odvodenina
  // ako `establishmentYear` v company-search, len počítaná na klientovi z už
  // načítaných dát (žiadny nový fetch).
  const establishedYear = (() => {
    const dates = [...(raw?.fullNames ?? []), ...(raw?.addresses ?? [])]
      .map((e) => e.validFrom)
      .filter((d): d is string => !!d)
      .sort();
    const first = dates[0];
    return first ? first.slice(0, 4) : null;
  })();

  const findings = buildFindings(riskScore, financialSummary, proceedingCount);

  // Staroba dát — jediný signál, ktorý dnes na stránke chýbal úplne: ESET má na
  // profile závierku 2021 bez akéhokoľvek varovania. Prah 18 mesiacov od konca
  // účtovného obdobia; závierka za minulý rok sa v RÚZ bežne objaví až v druhej
  // polovici nasledujúceho roka, takže kratší prah by kričal aj pri zdravých dátach.
  const staleYears = latest ? new Date().getFullYear() - latest.period : null;
  const financialsStale = staleYears != null && staleYears >= 2;

  // Zoznam pre bočnú lištu — teraz skutočné podstránky (`/firma/:ico/<key>`,
  // '' = Úvod), nie kotvy v jednom scrolli (2026-07-24, user feedback +
  // referencia na finstat.sk, ktoré má presne tento vzor: `/ico`, `/ico/
  // suvaha`, `/ico/financne_ukazovatele`...). Prvých 5 nesie názvoslovie z
  // mockupu "Vidíš skrz" (Úvod = Signály, Účtovná závierka, Odvetvový test,
  // Vlastníci, Prepojenia), zvyšok sú ďalšie reálne dátové zdroje, ktoré
  // ilustratívny mockup nepokrýval.
  const sections: DocSection[] = [];
  sections.push({
    key: '',
    label: 'Úvod',
    status:
      (riskScore && riskScore.score >= 20) ||
      (spDebt ?? 0) > 0 ||
      (vszpDebt ?? 0) > 0 ||
      proceedingCount > 0 ||
      hasDisqualified
        ? 'bad'
        : 'ok',
  });
  if (financialSummary)
    sections.push({
      key: 'uctovna-zavierka',
      label: 'Účtovná závierka',
      status: latest ? (financialsStale ? 'bad' : 'ok') : undefined,
    });
  if (benchmark && benchmark.ratioPeerCount >= 3)
    sections.push({ key: 'odvetvovy-test', label: 'Odvetvový test' });
  if (crzContracts && crzContracts.length > 0)
    sections.push({ key: 'zmluvy', label: 'Zmluvy so štátom' });
  if (
    (companyAuctions && companyAuctions.length > 0) ||
    (companyExecutions && companyExecutions.length > 0)
  )
    sections.push({
      key: 'majetok',
      label: 'Dražby a exekúcie',
      status: companyExecutions && companyExecutions.length > 0 ? 'bad' : undefined,
    });
  sections.push({ key: 'zaklad', label: 'Základné údaje' });
  if (hasRegistryHistory) sections.push({ key: 'historia', label: 'História zápisov' });
  return (
    <main className="company-page">
      {contentLoading && (
        <div className="company-body">
          <CompanySkeleton />
        </div>
      )}
      {error && (
        <div className="company-body">
          <p style={{ color: 'var(--risk-high)' }}>{(error as Error).message}</p>
        </div>
      )}

      {data && !contentLoading && (
        <>
          {/* --- Zóna 0: navigácia úplne navrch -------------------------------
              Presunutá pred hlavičku (2026-07-25) — predtým bola lišta až pod
              identitou firmy, takže pri príchode na stránku nebola vidno bez
              scrollu. Lepivá (top:64) drží zvyšok stránky pod topbarom. */}
          <SectionRail
            ico={ico!}
            sections={sections}
            active={activeSection}
            isWatching={isWatching}
            watchLoading={watchLoading}
            onToggleWatch={() => void toggleWatch()}
            figures={null}
          />

          {/* --- Zóna A: prístrojová hlavička ---------------------------------
              Identita + verdikt + pás kľúčových čísel v jednom tmavom páse.
              Nahrádza `.identity` + RiskRing + `.verdict-band` — riziko sa
              predtým zobrazovalo trikrát (pilulka, prstenec, slovo) a nad
              prehybom nebolo ani jedno číslo. */}
          <header className="masthead-company">
            <div className="masthead-company-inner">
              {/* --- Papierová karta: identita + verdikt v jednom "výpise",
                  vždy krémová (--card-* tokeny), aj v tmavej téme stránky —
                  fyzický dokument vytiahnutý zo spisu (mockup "Vidíš skrz").
                  Na celú šírku (2026-07-25) — predtým bola v dvojstĺpci so
                  signálmi, čo nechávalo kartu prázdnu v strede; signály sú
                  teraz v riadku štvorcov nižšie spolu so Základnými údajmi. */}
              <div className="paper-card paper-card--wide">
                <div className="paper-card-top">
                  <span>Register výpis · RPO</span>
                  <span className="num">IČO {data.ico}</span>
                  </div>
                  <h1>{data.name ?? data.ico}</h1>
                  <div className="paper-card-kind">
                    {data.naceName}
                    {data.naceName && data.address?.city && ' · '}
                    {data.address?.street ? `${data.address.street}, ` : ''}
                    {data.address?.city}
                    {establishedYear && ` · od ${establishedYear}`}
                    {data.terminatedAt && (
                      <span className="masthead-company-tag terminated" style={{ marginLeft: 8 }}>
                        Zaniknutá {formatSkDate(data.terminatedAt)}
                      </span>
                    )}
                  </div>

                  {riskScore && (
                    <div className="paper-risk-row">
                      <span className={`prr-word ${riskBand(riskScore.score)}`}>
                        {riskWord(riskScore.score)}
                      </span>
                      <div className={`prr-meter ${riskBand(riskScore.score)}`}>
                        <span
                          style={{ width: `${Math.max(2, Math.min(100, riskScore.score))}%` }}
                        />
                      </div>
                      <span className="num" style={{ fontSize: '0.78rem', color: 'var(--card-muted)' }}>
                        {riskScore.score}/100
                      </span>
                      <span className="prr-altman">
                        Altman{' '}
                        <b>
                          {riskScore.altmanZScore ? riskScore.altmanZScore.score : '—'}
                        </b>
                        {riskScore.altmanZScore && ` · ${altmanZoneWord(riskScore.altmanZScore.zone)}`}
                      </span>
                    </div>
                  )}

                  <div className="paper-card-actions no-print">
                    <button
                      type="button"
                      onClick={() => void toggleWatch()}
                      disabled={watchLoading}
                      className={isWatching ? 'ghost' : ''}
                    >
                      {isWatching ? '✓ Sledujem' : '+ Sledovať'}
                    </button>
                    <button type="button" onClick={() => window.print()} className="ghost">
                      Stiahnuť PDF
                    </button>
                    {watchError && <span className="masthead-company-error">{watchError}</span>}
                  </div>
                </div>

              {/* --- Riadok štvorcov: Základné údaje (celý výpis, zdieľaný s
                  tabom cez RegistryFacts) + Záväzky voči štátu vedľa seba —
                  predtým bolo druhé v úzkom stĺpci vedľa karty, teraz sú
                  rovnako veľké a "uhladené" (2026-07-25, user feedback: chcel
                  tu celý výpis, nie skrátený výber). --- */}
              <div className="masthead-facts-row">
                <aside className="signals-rail">
                  <div className="signals-rail-head">Základné údaje</div>
                  <RegistryFacts data={data} />
                  <Link to={`/firma/${ico}/zaklad`} className="signals-rail-more no-print">
                    Vlastníci, prepojenia a viac →
                  </Link>
                </aside>

                {/* --- Signály: to isté, čo predtým nieslo "Záväzky voči štátu"
                    ďaleko pod prehybom, teraz hneď vedľa identity. Detailné
                    podtabuľky (vestník, RPVS udalosti) ostávajú nižšie. --- */}
                <aside className="signals-rail">
                  <div className="signals-rail-head">Záväzky voči štátu</div>
                  <div className="status-grid status-grid--rail">
                    <StatusCell
                      label="Sociálna poisťovňa"
                      tone={(spDebt ?? 0) > 0 ? 'bad' : 'ok'}
                      value={(spDebt ?? 0) > 0 ? formatEur(spDebt) : 'Bez dlhu'}
                    />
                    <StatusCell
                      label="VšZP"
                      tone={(vszpDebt ?? 0) > 0 ? 'bad' : 'ok'}
                      value={(vszpDebt ?? 0) > 0 ? formatEur(vszpDebt) : 'Bez dlhu'}
                    />
                    <StatusCell
                      label="Finančná správa — DPH"
                      tone={data.hasDphCancellationReason ? 'warn' : 'ok'}
                      value={data.hasDphRegistration ? 'Platca' : 'Nie je platca'}
                      note={
                        data.hasDphCancellationReason
                          ? 'dôvod na zrušenie registrácie'
                          : data.taxReliabilityIndex ?? undefined
                      }
                    />
                    <StatusCell
                      label="Insolvenčné konania"
                      tone={proceedingCount > 0 ? 'bad' : 'ok'}
                      value={proceedingCount > 0 ? `${proceedingCount}` : 'Žiadne'}
                    />
                    <StatusCell
                      label="Diskvalifikované osoby"
                      tone={hasDisqualified ? 'bad' : 'ok'}
                      value={hasDisqualified ? 'Evidovaná' : 'Žiadna'}
                    />
                    <StatusCell
                      label="Zápis v RPVS"
                      tone="neutral"
                      value={data.hasRpvsRegistration ? 'Áno' : 'Nie'}
                    />
                    <StatusCell
                      label="Exekúcie"
                      tone={
                        companyExecutions && companyExecutions.length > 0
                          ? companyExecutions.every((e) => executionIsStopped(e.proposalMethod))
                            ? 'warn'
                            : 'bad'
                          : 'ok'
                      }
                      value={
                        companyExecutions && companyExecutions.length > 0
                          ? `${companyExecutions.length}`
                          : 'Žiadne'
                      }
                    />
                    <StatusCell
                      label="Sankčný zoznam EÚ"
                      tone={sanctionMatches && sanctionMatches.length > 0 ? 'bad' : 'ok'}
                      value={sanctionMatches && sanctionMatches.length > 0 ? 'Zhoda' : 'Bez zhody'}
                    />
                  </div>
                </aside>
              </div>

              {!latest && (
                <p className="masthead-figures-empty" style={{ marginTop: 16 }}>
                  Firma zatiaľ nezverejnila účtovnú závierku v RÚZ — finančné čísla sa objavia hneď,
                  ako budú dostupné. Registrové údaje nižšie sú kompletné.
                </p>
              )}
            </div>
          </header>

          {/* --- Zóna B: stužka aktuálnosti ----------------------------------
              Dáta na to boli, len sa nezobrazovali: ESET má na profile závierku
              2021 a nič to nenaznačovalo. Pri hodnotení bonity je to zásadné. */}
          {financialsStale && latest && (
            <div className="staleness-ribbon">
              <b>Aktuálnosť</b>
              <span>
                Posledná závierka v RÚZ je za rok <strong className="num">{latest.period}</strong> —{' '}
                {staleYears} {staleYears === 1 ? 'rok' : staleYears! < 5 ? 'roky' : 'rokov'} stará.
                Novšia zatiaľ nebola zverejnená. Ostatné registre (insolvencie, dlžníci, zmluvy,
                dražby) sú synchronizované priebežne.
              </span>
            </div>
          )}

          {/* --- Zóna D: dokument (nav sa presunula úplne navrch, viď nad
              hlavičkou nižšie — 2026-07-25, user chcel lištu vidieť hneď,
              nie až po scrolli cez identitu firmy). --- */}
          <div className="company-body">
            <div className="company-doc">
              {activeSection === '' && findings.length > 0 && (
                <section id="zistenia" className="doc-section">
                  <div className="section-title">
                    <h2>Zistenia</h2>
                    <span className="hint">z 9 registrov · zoradené podľa závažnosti</span>
                  </div>
                  {/* Naratívna veta bola predtým v `hint` vpravo, kde sa lámala do
                      úzkeho stĺpca — je to lead sekcie, nie poznámka pod čiarou. */}
                  {narrative && <p className="doc-lede">{narrative}</p>}
                  <div className="fin-panel signal-panel">
                    {findings.map((f, i) => (
                      <div className="finding-row" key={i}>
                        <span className="finding-no num">{String(i + 1).padStart(2, '0')}</span>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* --- Rizikové skóre: prečo to číslo vyzerá tak, ako vyzerá.
                  Patrí hneď za Zistenia, lebo je ich vysvetlením. --- */}
              {activeSection === '' && riskScore && (
                <section id="riziko" className="doc-section">
                  <div className="section-title">
                    <h2>Rizikové skóre</h2>
                    <span className="hint">
                      <a href="/metodika">ako sa počíta →</a>
                    </span>
                  </div>
                  <div className="risk-band">
                    <div className="risk-score-block">
                      <RiskGauge score={riskScore.score} signalCount={riskScore.signals.length} />
                    </div>
                    <div className="signal-list">
                      <div className="signal-list-title">Prečo toto skóre</div>
                      {riskScore.signals.length === 0 && (
                        <p style={{ color: 'var(--slate)', fontSize: '0.87rem', margin: 0 }}>
                          Žiadne rizikové signály nezachytené. Skóre 0 znamená, že firma neprepadla
                          ani v jednej z kontrol nižšie — nie že je bez rizika ako taká.
                        </p>
                      )}
                      {riskScore.signals.map((s, i) => (
                        <div className="signal-row" key={i}>
                          <div className={`signal-stripe ${signalSeverity(s.points)}`} />
                          <div className="signal-text">
                            <b>{s.label}</b>
                            <span className="sub">{s.detail}</span>
                          </div>
                          <div className="signal-weight num">+{s.points}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {riskScore.altmanZScore && <AltmanScale altman={riskScore.altmanZScore} />}

                  {sanctionMatches && sanctionMatches.length > 0 && (
                    <div className="fin-panel" style={{ padding: '16px 22px', marginTop: 14 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.6rem',
                          marginBottom: 8,
                        }}
                      >
                        <span className="pill high">Zhoda v sankčnom zozname</span>
                        <b>Sankčná kontrola (EU)</b>
                      </div>
                      <p style={{ color: 'var(--slate)', fontSize: '0.82rem', margin: '0 0 12px' }}>
                        Meno sa zhoduje so záznamom v konsolidovanom sankčnom zozname EÚ. Ide o
                        zhodu mena, nie o potvrdenú totožnosť — over si to pred akýmkoľvek
                        rozhodnutím, mená sa môžu zhodovať aj náhodne.
                      </p>
                      <ul className="list-plain">
                        {sanctionMatches.map((m, i) => (
                          <li key={i} style={{ fontSize: '0.87rem' }}>
                            <b>{m.matchedName}</b> — zhoda s "{m.sanctionedName}"
                            {m.programme ? ` (${m.programme})` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>
              )}

              {/* --- Záväzky voči štátu: najrýchlejší diskvalifikátor pri
                  obchodnom rozhodnutí, preto nad financiami. Stavová mriežka
                  nahrádza štyri rovnaké zelené "NIE" pilulky z verdikt pásu —
                  keď je všetko v poriadku, nesú takmer nulovú informáciu. --- */}
              {activeSection === '' && (
              <section id="zavazky" className="doc-section">
                <div className="section-title">
                  <h2>Záväzky voči štátu — detail</h2>
                  <span className="hint">
                    Súhrn je v paneli signálov v hlavičke. Tu plný riadkový výpis udalostí, ak nejaké sú.
                  </span>
                </div>

                {insolvency &&
                  (insolvency.events.length > 0 ||
                    insolvency.vszpDebts.length > 0 ||
                    insolvency.socpoistDebts.length > 0) && (
                    <div className="sub-block">
                      <h3>Obchodný vestník a register dlžníkov</h3>
                      <span className="hint">Slovensko.Digital</span>
                      <div className="panel">
                        <div className="overflow-x">
                          <table className="dense-table">
                            <thead>
                              <tr>
                                <th>Typ</th>
                                <th>Popis</th>
                                <th className="amt">Dátum</th>
                              </tr>
                            </thead>
                            <tbody>
                              {insolvency.events.map((e) => (
                                <tr key={e.id}>
                                  <td>
                                    <span
                                      className={`pill ${e.eventType === 'znizenie_imania' ? 'mid' : 'high'}`}
                                    >
                                      {INSOLVENCY_LABELS[e.eventType]}
                                    </span>
                                  </td>
                                  <td>
                                    {e.description ?? '—'}
                                    {(e.heading ??
                                      e.decisionText ??
                                      e.announcementText ??
                                      e.adviceText) && (
                                      <details style={{ marginTop: '0.3rem' }}>
                                        <summary
                                          style={{
                                            cursor: 'pointer',
                                            color: 'var(--slate)',
                                            fontSize: '0.85rem',
                                          }}
                                        >
                                          Čo sa deje / speňažuje — zobraziť text uznesenia
                                        </summary>
                                        <div
                                          style={{
                                            whiteSpace: 'pre-wrap',
                                            fontSize: '0.85rem',
                                            marginTop: '0.4rem',
                                            color: 'var(--slate)',
                                          }}
                                        >
                                          {e.heading && <p>{e.heading}</p>}
                                          {e.decisionText && <p>{e.decisionText}</p>}
                                          {e.announcementText && <p>{e.announcementText}</p>}
                                          {e.adviceText && <p>{e.adviceText}</p>}
                                        </div>
                                      </details>
                                    )}
                                  </td>
                                  <td className="amt">
                                    {e.eventDate ? e.eventDate.slice(0, 10) : '—'}
                                  </td>
                                </tr>
                              ))}
                              {insolvency.vszpDebts.map((d) => (
                                <tr key={d.id}>
                                  <td>
                                    <span className="pill mid">Dlžník VšZP</span>
                                  </td>
                                  <td>{formatEur(Number(d.amount))}</td>
                                  <td className="amt">{d.publishedOn.slice(0, 10)}</td>
                                </tr>
                              ))}
                              {insolvency.socpoistDebts.map((d) => (
                                <tr key={d.id}>
                                  <td>
                                    <span className="pill mid">Dlžník Sociálnej poisťovne</span>
                                  </td>
                                  <td>
                                    {formatEur(Number(d.amount))}
                                    {d.missingPeriods && (
                                      <div className="hint">
                                        Chýbajúce podklady: {d.missingPeriods}
                                      </div>
                                    )}
                                  </td>
                                  <td className="amt">—</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                {insolvency && insolvency.replikProceedings.length > 0 && (
                  <div className="sub-block">
                    <h3>Insolvenčné konania (IS REPLIK)</h3>
                    <span className="hint">Ministerstvo spravodlivosti SR, živé dáta</span>
                    <div className="panel">
                      <div className="overflow-x">
                        <table className="dense-table">
                          <thead>
                            <tr>
                              <th>Typ konania</th>
                              <th>Spisová značka</th>
                              <th>Súd</th>
                              <th>Správca</th>
                              <th>Stav</th>
                              <th className="amt">Začiatok</th>
                              <th className="amt">Ukončenie</th>
                            </tr>
                          </thead>
                          <tbody>
                            {insolvency.replikProceedings.map((p) => (
                              <tr key={p.id}>
                                <td>
                                  <span className="pill high">
                                    {REPLIK_TYP_LABELS[p.typ] ?? p.typ}
                                  </span>
                                </td>
                                <td>{p.spisovaZnackaSudu ?? '—'}</td>
                                <td>{p.sudNazov ?? '—'}</td>
                                <td>{p.spravca ?? '—'}</td>
                                <td>{p.stavKonania ?? '—'}</td>
                                <td className="amt">
                                  {formatSkDate(p.datumZacatiaKonania ?? p.datumZacatiaProcesu)}
                                </td>
                                <td className="amt">{formatSkDate(p.datumUkonceniaProcesu)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {insolvency && insolvency.rpvsEvents.length > 0 && (
                  <div className="sub-block">
                    <h3>RPVS — výmaz, sankcie, podnety</h3>
                    <span className="hint">Register partnerov verejného sektora</span>
                    <div className="panel">
                      <div className="overflow-x">
                        <table className="dense-table">
                          <thead>
                            <tr>
                              <th>Typ</th>
                              <th>Popis</th>
                              <th className="amt">Dátum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {insolvency.rpvsEvents.map((e) => (
                              <tr key={e.id}>
                                <td>
                                  <span
                                    className={`pill ${e.eventType === 'vymaz_ex_offo' ? 'high' : 'mid'}`}
                                  >
                                    {RPVS_EVENT_LABELS[e.eventType] ?? e.eventType}
                                  </span>
                                </td>
                                <td>{e.description ?? '—'}</td>
                                <td className="amt">{formatSkDate(e.eventDate)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </section>
              )}

              {/* --- Financie --- */}
              {activeSection === 'uctovna-zavierka' && financialSummary && (
                <section id="financie" className="doc-section">
                  <div className="section-title">
                    <h2>Financie</h2>
                    <span className="hint">
                      Zdroj: RÚZ
                      {financialSummary.length > 0
                        ? ` · ${financialSummary.length} ${financialSummary.length === 1 ? 'zverejnená závierka' : financialSummary.length < 5 ? 'zverejnené závierky' : 'zverejnených závierok'}, celá história voľne`
                        : ''}
                    </span>
                  </div>
                  {financialSummary.length === 0 && (
                    <div className="fin-panel" style={{ padding: '18px 22px' }}>
                      Spoločnosť zatiaľ nezverejnila účtovnú závierku v Registri účtovných závierok.
                    </div>
                  )}
                  {financialSummary.length > 0 && (
                    <div className="fin-panel">
                      {/* Šesticu súhrnných čísel (Tržby/VH/Aktíva/…) tu predtým
                          duplikovala `fact-table` s MetricTile — od 2026-07-20 sú
                          rovno v hlavičke stránky, takže tu ostáva len vývoj. */}
                      <div className="fin-chart-row">
                        <div>
                          <div className="fin-chart-label">Vlastné imanie</div>
                          <div className="fin-chart-value num">
                            {formatEur(financialSummary[0]?.equity ?? null)}
                            {(() => {
                              const d = delta(
                                financialSummary[0]?.equity,
                                financialSummary[1]?.equity,
                              );
                              return d ? (
                                <span className={`fin-chart-delta ${d.dir}`}>
                                  {d.dir === 'up' ? '▲' : '▼'} {d.pct}%
                                </span>
                              ) : null;
                            })()}
                          </div>
                          <Sparkline
                            values={[...financialSummary].reverse().map((f) => f.equity)}
                          />
                          <div className="spark-years">
                            {[...financialSummary]
                              .reverse()
                              .map((f, i, arr) =>
                                i === 0 ||
                                i === arr.length - 1 ||
                                i === Math.floor(arr.length / 2) ? (
                                  <span key={f.period}>{f.period}</span>
                                ) : (
                                  <span key={f.period} />
                                ),
                              )}
                          </div>
                        </div>
                        {trend && trend.length >= 2 && (
                          <div className="no-print">
                            <div className="fin-chart-label">
                              Trend: tržby / výsledok hosp. / Altman Z
                            </div>
                            <TrendChart points={trend} />
                          </div>
                        )}
                      </div>
                      <div className="overflow-x">
                        <table className="fin-table">
                          <thead>
                            <tr>
                              <th>Rok</th>
                              <th>Aktíva</th>
                              <th>Vlastné imanie</th>
                              <th>Záväzky</th>
                              <th>Výsledok hosp.</th>
                              <th>Tržby</th>
                              <th>EBIT</th>
                              <th>EBITDA</th>
                              <th>ROA</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Celá história vždy voľne viditeľná — "kompletná história financií
                            vždy zadarmo" je zdokumentovaný diferenciátor voči Finstat paywallu
                            (docs/00 §4), predošlý 3-ročný CSS-zámok (.pro-locked-cell) nemal
                            žiadnu reálnu entitlement kontrolu a priamo mu protirečil. */}
                            {financialSummary.map((f) => (
                              <tr key={f.period}>
                                <td>{f.period}</td>
                                <td className="num">{formatEur(f.totalAssets)}</td>
                                <td className="num">{formatEur(f.equity)}</td>
                                <td className="num">{formatEur(f.liabilities)}</td>
                                <td
                                  className="num"
                                  style={{
                                    color:
                                      f.netIncome != null && f.netIncome < 0
                                        ? 'var(--risk-high)'
                                        : undefined,
                                  }}
                                >
                                  {formatEur(f.netIncome)}
                                </td>
                                <td className="num">{formatEur(f.revenue)}</td>
                                <td className="num">{formatEur(f.ebit)}</td>
                                <td className="num">{formatEur(f.ebitda)}</td>
                                <td className="num">
                                  {f.roa != null ? `${(f.roa * 100).toFixed(1)} %` : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {financialSummary.length > 0 && (
                    <div className="sub-block">
                      <h3>Zloženie súvahy</h3>
                      <span className="hint">Rok {financialSummary[0]?.period}, zdroj: RÚZ</span>
                      <div className="fin-panel">
                        <BalanceComposition year={financialSummary[0]!} />
                      </div>
                    </div>
                  )}
                  {financialSummary.length > 0 && (
                    <>
                      <div className="sub-block">
                        <h3>Súvaha a výkaz ziskov a strát</h3>
                        <span className="hint">Kompletný riadkový výpis, zdroj: RÚZ</span>
                        <FullStatementsPanel loading={fullStatementsLoading} years={fullStatements} />
                      </div>
                    </>
                  )}
                </section>
              )}

              {/* Kľúčové čísla — presunuté sem z lišty (2026-07-25), hneď pred
                  porovnanie s odvetvím, kam obsahovo patria (vlastné číslo než
                  medián firiem v odbore). */}
              {activeSection === 'odvetvovy-test' && railFigures && (
                <div className="rail-figures rail-figures--inline">
                  <RailFigure label="Tržby" f={railFigures.revenue} />
                  <RailFigure label="Výsledok hosp." f={railFigures.netIncome} />
                  <RailFigure label="Vlastné imanie" f={railFigures.equity} />
                  <RailFigure label="Záväzky" f={railFigures.liabilities} />
                  <RailFigure label="Zadlženosť" f={railFigures.debtRatio} />
                  <RailFigure label="Altman Z" f={railFigures.altmanZ} />
                </div>
              )}

              {/* --- Porovnanie s odvetvím: bez mediánu NACE je absolútne číslo
                  bezcenné, preto hneď za financiami. Zlúčené z dvoch miest —
                  benchmark bary boli v Prehľade, tabuľka priemerov vo Financiách,
                  každá s inou definíciou "odvetvia". Pro zámok odstránený: bol
                  čisto CSS bez entitlement kontroly a protirečil "kompletné dáta
                  zadarmo" (rovnaký prípad ako 3-ročný zámok vo fin-table vyššie). */}
              {activeSection === 'odvetvovy-test' &&
                !benchmarkLoading &&
                (!benchmark || benchmark.ratioPeerCount < 3) && (
                  <EmptySection
                    label="Porovnanie s odvetvím"
                    message="Pre toto odvetvie zatiaľ nemáme dosť porovnateľných firiem (aspoň 3) na zmysluplný medián — skús sa vrátiť neskôr, ako pribudnú ďalšie závierky."
                  />
                )}
              {activeSection === 'odvetvovy-test' && benchmark && benchmark.ratioPeerCount >= 3 && (
                <section id="odvetvie" className="doc-section">
                  <div className="section-title">
                    <h2>Porovnanie s odvetvím</h2>
                    <span className="hint">
                      {benchmark.naceName} · medián z{' '}
                      {benchmark.ratioPeerCount.toLocaleString('sk-SK')} firiem s financiami
                    </span>
                  </div>
                  <div className="fin-panel" style={{ padding: '16px 22px' }}>
                    <div className="bench-legend" style={{ marginBottom: 10 }}>
                      <span className="own-swatch" /> táto firma ·{' '}
                      <span className="median-swatch" /> medián odvetvia
                    </div>
                    <BenchRow
                      label="Celková zadlženosť"
                      own={benchmark.ownDebtRatio}
                      median={benchmark.medianDebtRatio}
                    />
                    <BenchRow
                      label="ROE (rentabilita VI)"
                      own={benchmark.ownRoe}
                      median={benchmark.medianRoe}
                    />
                    <BenchRow
                      label="ROA (rentabilita aktív)"
                      own={benchmark.ownRoa}
                      median={benchmark.medianRoa}
                    />
                    <BenchRow
                      label="Zisková marža"
                      own={benchmark.ownMargin}
                      median={benchmark.medianMargin}
                    />
                  </div>

                  {financialSummary && financialSummary.length > 0 && (
                    <div className="sub-block">
                      <h3>Finančné ukazovatele</h3>
                      {/* Odkaz na metodiku nedopĺňame — RatioCategories si ho
                          renderuje sám nad tabuľkou ukazovateľov. */}
                      <span className="hint">Rok {financialSummary[0]?.period}, zdroj: RÚZ</span>
                      <div className="fin-panel">
                        <RatioCategories
                          current={financialSummary[0]!}
                          previous={financialSummary[1] ?? null}
                        />
                      </div>
                    </div>
                  )}

                  {benchmark.peerCount >= 3 && (
                    <div className="sub-block">
                      <h3>Priemery odvetvia v absolútnych číslach</h3>
                      <span className="hint">{benchmark.peerCount} firiem v odvetví</span>
                      <div className="overflow-x">
                        <table className="fin-table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Táto firma</th>
                              <th>Priemer odvetvia</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="label">Tržby</td>
                              <td className="num">{formatEur(benchmark.ownRevenue)}</td>
                              <td className="num">{formatEur(benchmark.avgRevenue)}</td>
                            </tr>
                            <tr>
                              <td className="label">Výsledok hosp.</td>
                              <td className="num">{formatEur(benchmark.ownNetIncome)}</td>
                              <td className="num">{formatEur(benchmark.avgNetIncome)}</td>
                            </tr>
                            <tr>
                              <td className="label">Vlastné imanie</td>
                              <td className="num">{formatEur(benchmark.ownEquity)}</td>
                              <td className="num">{formatEur(benchmark.avgEquity)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* --- Zmluvy so štátom: pri väčšine firiem prázdne, vtedy sa
                  sekcia nevykreslí vôbec (žiadny "žiadne záznamy" panel). --- */}
              {activeSection === 'zmluvy' &&
                !crzContractsLoading &&
                (!crzContracts || crzContracts.length === 0) && (
                  <EmptySection
                    label="Zmluvy so štátom"
                    message="Táto firma nemá v Centrálnom registri zmlúv (CRZ) evidovanú žiadnu zmluvu so štátom."
                  />
                )}
              {activeSection === 'zmluvy' && crzContracts && crzContracts.length > 0 && (
                <section id="zmluvy" className="doc-section">
                  <div className="section-title">
                    <h2>Zmluvy so štátom</h2>
                    <span className="hint">Centrálny register zmlúv</span>
                  </div>
                  <div className="fin-panel">
                    <div className="overflow-x">
                      <table className="dense-table">
                        <thead>
                          <tr>
                            <th>Rola</th>
                            <th>Protistrana</th>
                            <th>Predmet</th>
                            <th className="amt">Suma</th>
                            <th className="amt">Dátum podpisu</th>
                          </tr>
                        </thead>
                        <tbody>
                          {crzContracts.map((c) => {
                            const isAuthority = c.authorityCompanyId != null;
                            return (
                              <tr key={c.id}>
                                <td>
                                  <span className="pill mid">
                                    {isAuthority ? 'Objednávateľ' : 'Dodávateľ'}
                                  </span>
                                </td>
                                <td>
                                  {isAuthority
                                    ? (c.supplierName ?? '—')
                                    : (c.contractingAuthorityName ?? '—')}
                                </td>
                                <td>{c.subject ?? '—'}</td>
                                <td className="amt num">
                                  {c.priceAmount ? formatEur(Number(c.priceAmount)) : '—'}
                                </td>
                                <td className="amt">{c.signedOn ?? '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>
              )}

              {/* --- Dražby a exekúcie: zlúčené do jednej sekcie, obe sú "majetok
                  v tiesni" — oddelene pôsobili riedko a obe boli zahrabané v tabe
                  Profil, teda tam, kde ich nikto nehľadal. --- */}
              {activeSection === 'majetok' &&
                !companyAuctionsLoading &&
                !companyExecutionsLoading &&
                !(companyAuctions && companyAuctions.length > 0) &&
                !(companyExecutions && companyExecutions.length > 0) && (
                  <EmptySection
                    label="Dražby a exekúcie"
                    message="Pre túto firmu sme v Obchodnom vestníku nenašli žiadnu dražbu ani exekúciu."
                  />
                )}
              {activeSection === 'majetok' &&
                ((companyAuctions && companyAuctions.length > 0) ||
                (companyExecutions && companyExecutions.length > 0)) && (
                <section id="majetok" className="doc-section">
                  <div className="section-title">
                    <h2>Dražby a exekúcie</h2>
                    <span className="hint">Obchodný vestník</span>
                  </div>
                  {companyAuctions && companyAuctions.length > 0 && (
                    <div className="sub-block">
                      <h3>Dražby</h3>
                      <span className="hint">Firma ako dražobník/organizátor</span>
                      <div className="fin-panel">
                        <div className="overflow-x">
                          <table className="dense-table">
                            <thead>
                              <tr>
                                <th>Typ</th>
                                <th>Predmet</th>
                                <th className="amt">Dátum konania</th>
                              </tr>
                            </thead>
                            <tbody>
                              {companyAuctions.map((a) => (
                                <tr key={a.id}>
                                  <td>
                                    <span className="pill mid">
                                      {a.auctionType === 'exekucna'
                                        ? 'Exekučná dražba'
                                        : 'Dobrovoľná dražba'}
                                    </span>
                                  </td>
                                  <td>{a.predmet ?? a.propertyType ?? '—'}</td>
                                  <td className="amt">{a.auctionDate ?? '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {companyExecutions && companyExecutions.length > 0 && (
                    <div className="sub-block">
                      <h3>Exekúcie</h3>
                      <span className="hint">len záznamy, kde je firma dlžníkom</span>
                      <div className="fin-panel">
                        <div className="overflow-x">
                          <table className="dense-table">
                            <thead>
                              <tr>
                                <th>Stav</th>
                                <th>Spisová značka</th>
                                <th>Exekútor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {companyExecutions.map((e) => (
                                <tr key={e.id}>
                                  <td>
                                    <span
                                      className={`pill ${executionIsStopped(e.proposalMethod) ? 'low' : 'mid'}`}
                                    >
                                      {executionMethodLabel(e.proposalMethod)}
                                    </span>
                                  </td>
                                  <td>{e.caseNumber ?? '—'}</td>
                                  <td>{e.executorName ?? '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* --- Základné údaje: samotný výpis (RegistryFacts) je od
                  2026-07-25 v hlavičke vedľa Záväzkov voči štátu, viditeľný na
                  každom tabe — tu by bol duplicitne (user feedback). Tab
                  necháva len to, čo v hlavičke nie je: vlastníkov, prepojenia
                  a externé odkazy. --- */}
              {activeSection === 'zaklad' && (
              <section id="zaklad" className="doc-section">
                {/* --- Vlastníci a Prepojenia: zlúčené sem zo samostatných tabov
                    (2026-07-25, user feedback — pôsobili ako duplicity vedľa
                    Základných údajov). RPO orgány (z hrán grafu) + RPVS/RPO
                    spoločníci na jednom mieste, graf väzieb hneď pod nimi. --- */}
                <div className="sub-block" id="vlastnici">
                  <h3>Vlastníci a štatutári</h3>
                  <span className="hint">
                    RPO štatutári/orgány · RPVS koneční užívatelia výhod
                  </span>
                  {companyPersonsLoading && directPeople.length === 0 && (
                    <p className="hint" style={{ display: 'block', marginTop: 8 }}>
                      Načítavam štatutárov…
                    </p>
                  )}
                  {!companyPersonsLoading &&
                    directPeople.length === 0 &&
                    (raw?.stakeholders ?? []).length === 0 && (
                      <p className="hint" style={{ display: 'block', marginTop: 8 }}>
                        Register neeviduje žiadnych štatutárov ani konečných užívateľov výhod —
                        pri niektorých právnych formách (napr. fyzická osoba - podnikateľ) je to
                        bežné, inak môže ísť o neúplné registrové dáta.
                      </p>
                    )}

                  {directPeople.length > 0 && <PeopleRoster people={directPeople} />}

                  {(raw?.stakeholders ?? []).filter((s) => s.personName?.formatedName).length >
                    0 &&
                    (() => {
                      const sharesByName = new Map(
                        (ownership?.shareholders ?? []).map((s) => [
                          normalizePersonNameLoose(s.name),
                          s.sharePct,
                        ]),
                      );
                      // Zdroj % podielu (Obchodný vestník cez Slovensko.Digital)
                      // prestal pribúdať niekde v priebehu 2022 (2026-07-26
                      // zistenie pri stavaní syncu — ostatné ov.* feedy sú
                      // čerstvé, tento konkrétny nie) — zápis teda môže byť
                      // starší, než pôsobí, ak sa štruktúra odvtedy zmenila.
                      const ownershipYears = ownership?.filedAt
                        ? new Date().getFullYear() - new Date(ownership.filedAt).getFullYear()
                        : null;
                      const ownershipStale = ownershipYears != null && ownershipYears >= 3;
                      return (
                    <div className="sub-block">
                      <h3>Spoločníci</h3>
                      <span className="hint">
                        {ownership
                          ? `Zdroj: RPO (osoby) · Obchodný vestník (% podielu, zápis z ${formatSkDate(ownership.filedAt)})`
                          : 'Zdroj: RPO — bez % podielu a výšky vkladu, tie poskytuje len ORSR'}
                      </span>
                      {ownershipStale && (
                        <span className="hint" style={{ display: 'block', color: 'var(--risk-mid)' }}>
                          Zápis je {ownershipYears} {ownershipYears === 1 ? 'rok' : ownershipYears! < 5 ? 'roky' : 'rokov'} starý — ak sa vlastnícka štruktúra odvtedy zmenila, % podielu už nemusí sedieť. Aktuálny stav overíš v ORSR (odkaz nižšie).
                        </span>
                      )}
                      <div className="fin-panel" style={{ padding: '18px 22px' }}>
                        <table className="dense-table">
                          <thead>
                            <tr>
                              <th>Osoba</th>
                              <th>Vzťah</th>
                              <th>Adresa</th>
                              {ownership && <th className="amt">% podielu</th>}
                              <th className="amt">Od</th>
                              <th className="amt">Do</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(raw?.stakeholders ?? [])
                              .filter((s) => s.personName?.formatedName)
                              .sort((a, b) => (b.validFrom ?? '').localeCompare(a.validFrom ?? ''))
                              .map((s, i) => {
                                const sharePct = sharesByName.get(
                                  normalizePersonNameLoose(s.personName!.formatedName!),
                                );
                                return (
                                <tr key={i} style={{ opacity: s.validTo ? 0.65 : undefined }}>
                                  <td>{s.personName?.formatedName}</td>
                                  <td>{s.stakeholderType?.value ?? '—'}</td>
                                  <td>
                                    {s.address?.municipality?.value ?? '—'}
                                    {s.address?.country?.code &&
                                      s.address.country.code !== '703' && (
                                        <span className="hint"> ({s.address.country.value})</span>
                                      )}
                                  </td>
                                  {ownership && (
                                    <td className="amt num">
                                      {sharePct != null ? `${sharePct} %` : '—'}
                                    </td>
                                  )}
                                  <td className="amt num">{formatSkDate(s.validFrom)}</td>
                                  <td className="amt num">
                                    {s.validTo ? formatSkDate(s.validTo) : 'aktuálny'}
                                  </td>
                                </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                      );
                    })()}
                </div>

                {/* --- Prepojenia: graf väzieb je signature prvok identity E-Analytika,
                preto ostáva vizuálne samostatný blok aj po zlúčení tabov. --- */}
                <div className="sub-block" id="prepojenia">
                  <h3>Prepojenia</h3>
                  <span className="hint">RPO štatutári/orgány · RPVS UBO</span>
                  {!networkLoading && (!network || network.nodes.length === 0) && (
                    <p className="hint" style={{ display: 'block', marginTop: 8 }}>
                      Zatiaľ sme nenašli žiadne prepojenia na osoby cez register (štatutári,
                      spoločníci) — môže ísť o neúplné registrové dáta, nie nutne o firmu bez
                      väzieb.
                    </p>
                  )}
                  {network && network.nodes.length > 0 && (
                    <>
                      <div
                        className="no-print"
                        style={{
                          display: 'flex',
                          gap: '1rem',
                          alignItems: 'center',
                          margin: '0.75rem 0',
                          flexWrap: 'wrap',
                        }}
                      >
                        <label style={{ fontSize: '0.85rem', color: 'var(--slate)' }}>
                          <input
                            type="checkbox"
                            checked={showHistorical}
                            onChange={(e) => setShowHistorical(e.target.checked)}
                          />{' '}
                          zobraziť aj historické väzby
                        </label>
                        <label style={{ fontSize: '0.85rem', color: 'var(--slate)' }}>
                          hĺbka:{' '}
                          <select value={depth} onChange={(e) => setDepth(Number(e.target.value))}>
                            <option value={1}>1</option>
                            <option value={2}>2</option>
                            <option value={3}>3</option>
                          </select>
                        </label>
                      </div>
                      {network.nodes.some((n) => n.type === 'person' && !n.identityConfirmed) && (
                        <p className="hint no-print" style={{ marginBottom: '0.75rem' }}>
                          Osoby s prerušovaným obrysom (označené „?“) nemajú v RPO uvedený dátum
                          narodenia — ich identita nie je potvrdená, len zhoda mena. Graf ich preto
                          nerozvetvuje ďalej k iným firmám, aby neukazoval prepojenie, ktoré v
                          skutočnosti nemusí existovať.
                        </p>
                      )}
                      <p className="hint" style={{ display: 'none' }} data-print-only>
                        Interaktívny graf prepojení dostupný na radar.marstein.sk/firma/{ico}.
                      </p>
                      <div className="fin-panel no-print">
                        <NetworkGraph
                          graph={network}
                          showHistorical={showHistorical}
                          onSelectNode={(nodeId) => {
                            const [type, id] = nodeId.split(':');
                            if (type === 'company') {
                              const node = network.nodes.find((n) => n.id === nodeId);
                              if (node?.ico) navigate(`/firma/${node.ico}`);
                            } else {
                              const node = network.nodes.find((n) => n.id === nodeId);
                              if (node) {
                                setSelectedPerson({
                                  id: id!,
                                  fullName: node.label,
                                  givenNames: null,
                                  familyNames: null,
                                  birthDate: null,
                                  fingerprint: '',
                                  createdAt: '',
                                  updatedAt: '',
                                });
                              }
                            }
                          }}
                        />
                      </div>
                      {selectedPerson && (
                        <div
                          className="fin-panel"
                          style={{ marginTop: '0.75rem', padding: '16px 20px' }}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedPerson(null)}
                            style={{
                              ...buttonStyle,
                              background: 'transparent',
                              color: 'var(--brand)',
                              marginBottom: '0.5rem',
                            }}
                          >
                            ← zavrieť
                          </button>
                          {network?.nodes.find((n) => n.id === `person:${selectedPerson.id}`)
                            ?.identityConfirmed === false && (
                            <p className="hint" style={{ marginBottom: '0.5rem' }}>
                              Táto osoba nemá v RPO uvedený dátum narodenia — zoznam nižšie môže
                              obsahovať aj inú osobu s rovnakým menom, nie nutne tú istú.
                            </p>
                          )}
                          <h4 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>
                            {selectedPerson.fullName}
                            <span
                              style={{ color: 'var(--slate)', fontSize: '0.8rem', fontWeight: 400 }}
                            >
                              {' '}
                              — kde inde pôsobí
                            </span>
                          </h4>
                          {personCompaniesLoading && <p>Načítavam firmy...</p>}
                          {personCompanies && personCompanies.length === 0 && (
                            <p>Žiadne ďalšie firmy nenájdené.</p>
                          )}
                          {personCompanies && personCompanies.length > 0 && (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              {personCompanies.map(({ role, company }) => (
                                <li key={role.id}>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/firma/${company.ico}`)}
                                    style={personResultStyle}
                                  >
                                    {company.name ?? company.ico}
                                    <span style={{ color: 'var(--slate)', fontSize: '0.8rem' }}>
                                      {' '}
                                      · {role.roleLabel}
                                      {role.validTo ? ` (do ${role.validTo})` : ''}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {similar && similar.length > 0 && (
                        <div className="sub-block no-print">
                          <h3>Podobné firmy</h3>
                          <span className="hint">rovnaké mesto</span>
                          <div className="fin-panel" style={{ padding: '0 22px' }}>
                            <ul className="list-plain">
                              {similar.map((c) => (
                                <li key={c.ico}>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/firma/${c.ico}`)}
                                    style={personResultStyle}
                                  >
                                    {c.name ?? c.ico}
                                    <span
                                      className="num"
                                      style={{ color: 'var(--slate)', fontSize: '0.8rem' }}
                                    >
                                      {' '}
                                      · IČO {c.ico}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      {sameAddress && sameAddress.length > 0 && (
                        <div className="sub-block no-print">
                          <h3>Iné firmy na tejto adrese</h3>
                          <span className="hint">
                            zdieľané sídlo samo osebe nie je pochybenie — bežné napr. pri
                            účtovníckych adresách alebo skupine firiem
                          </span>
                          <div className="fin-panel" style={{ padding: '0 22px' }}>
                            <ul className="list-plain">
                              {sameAddress.map((c) => (
                                <li key={c.ico}>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/firma/${c.ico}`)}
                                    style={personResultStyle}
                                  >
                                    {c.name ?? c.ico}
                                    <span
                                      className="num"
                                      style={{ color: 'var(--slate)', fontSize: '0.8rem' }}
                                    >
                                      {' '}
                                      · IČO {c.ico}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      {companyListings && companyListings.length > 0 && (
                        <div className="sub-block no-print">
                          <h3>Súvisiace inzeráty</h3>
                          <span className="hint">pridané používateľmi, viď /inzeraty</span>
                          <div className="listing-grid">
                            {companyListings.map((l) => (
                              <div
                                key={l.id}
                                className="listing-card"
                                onClick={() => navigate(`/inzeraty/${l.id}`)}
                              >
                                {l.images.length > 0 ? (
                                  <img
                                    className="listing-card-image"
                                    src={`/uploads/${l.images[0]!.filename}`}
                                    alt={l.title}
                                  />
                                ) : (
                                  <div className="listing-card-image-placeholder">Bez fotky</div>
                                )}
                                <div className="listing-card-body">
                                  <div>{l.title}</div>
                                  <div className="listing-card-price">
                                    {l.priceCents != null
                                      ? `${(l.priceCents / 100).toLocaleString('sk-SK')} €`
                                      : 'Cena dohodou'}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="sub-block">
                  <h3>Externé zdroje</h3>
                  <div className="fin-panel" style={{ padding: '18px 22px' }}>
                    <p className="hint" style={{ marginBottom: 10 }}>
                      Priamy odkaz na originálny záznam v štátnom registri, mimo Corpuls.
                    </p>
                    <ul className="list-plain">
                      <li>
                        <a
                          href={`https://www.orsr.sk/hladaj_ico.asp?ICO=${data.ico}&SID=0`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Obchodný register (ORSR) — výpis podľa IČO ↗
                        </a>
                      </li>
                      {data.hasRpvsRegistration && (
                        <li>
                          <a
                            href="https://rpvs.gov.sk/rpvs/Partner/Partner/VyhladavaniePartnera"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Register partnerov verejného sektora (RPVS) — vyhľadať IČO {data.ico} ↗
                          </a>
                        </li>
                      )}
                      <li>
                        <a
                          href="https://www.justice.gov.sk/pod/ov.nsf/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Obchodný vestník — fulltextové vyhľadávanie ↗
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
                <BusinessActivities data={data} />
              </section>
              )}

              {/* --- História zápisov: archívna vrstva, uzatvára dokument. --- */}
              {activeSection === 'historia' && !hasRegistryHistory && (
                <EmptySection
                  label="História zápisov"
                  message="Pre túto firmu register nezaznamenal žiadnu históriu zmien názvu, sídla ani predmetov podnikania."
                />
              )}
              {activeSection === 'historia' && hasRegistryHistory && (
                <RegistryChangeHistory data={data} />
              )}
            </div>
          </div>

          <footer className="attribution">
            <b>Zdroje dát:</b> Register právnických osôb (ŠÚ SR, CC-BY 4.0) · Register účtovných
            závierok (MF SR, CC0) · Register partnerov verejného sektora · Služby Slovensko.Digital
            (Register právnických osôb V2, Obchodný vestník, Register dlžníkov VšZP) — dáta majú
            informatívny charakter, nie sú právne záväzné.
          </footer>
        </>
      )}
    </main>
  );
}

// Zoznam osôb naviazaných na firmu (2026-07-20). Predtým sa vypisovala každá
// hrana grafu ako vlastný riadok — pri ESETe 44 riadkov pre 13 skutočných ľudí,
// pretože RPO tú istú väzbu eviduje viackrát a historické väzby boli premiešané
// s aktuálnymi. Teraz: jeden riadok = jeden človek, roly zlúčené do stĺpca,
// bývalé väzby v samostatnom zbalenom bloku (jediná výnimka z "nič za
// rozklikom" popri archívnych závierkach — je to archív, nie aktuálny stav).
// Tá istá osoba sa v zozname objaví dvakrát, keď ju vedie RPO aj RPVS — RPO
// s akademickým titulom ("Ing. Peter Paško", rola štatutára), RPVS bez neho
// ("Peter Paško", konečný užívateľ výhod). Sú to samostatné záznamy s vlastným
// id, zlúčiť ich podľa mena by znamenalo riskovať zlúčenie dvoch rôznych ľudí
// s rovnakým menom. Namiesto toho stĺpec Zdroj rovno povie, prečo tam sú dva
// riadky — vyzerá to potom ako údaj, nie ako chyba.
const SOURCE_LABELS: Record<string, string> = {
  rpo: 'RPO',
  rpvs: 'RPVS',
};

interface PersonRoles {
  key: string;
  label: string;
  identityConfirmed: boolean | null | undefined;
  roles: string[];
  /** Registre, z ktorých väzba pochádza (rpo = štatutári/spoločníci, rpvs = UBO). */
  sources: string[];
  /** Najstarší začiatok spomedzi zlúčených väzieb. */
  since: string | null;
  /** Najnovší koniec; null = väzba stále trvá. */
  until: string | null;
}

// Bežné SK akademické tituly — RPO ("Ing. Peter Paško") a RPVS UBO ("Peter Paško")
// vedia tú istú osobu uviesť raz s titulom, raz bez, a keďže RPO nikdy neposiela
// dátum narodenia a RPVS vždy áno, dostanú v `persons` tabuľke vždy INÝ fingerprint
// (persons/fingerprint.ts) — teda dve odlišné osoby v dátach, hoci ide o tú istú.
// V rámci JEDNEJ firmy (táto funkcia dostáva vždy len jednu) je zhoda mena po
// odstránení titulu prakticky vždy tá istá osoba — bezpečné zlúčiť tu, na rozdiel
// od grafu prepojení naprieč rôznymi firmami (2026-07-25, user feedback).
// Pozor: `\b` nemôže nastať medzi dvomi nie-slovnými znakmi ("." a medzera), preto
// voliteľná bodka za titulom nesmie byť vnútri `\.?` pred `\b` (bug objavený
// 2026-07-25 — "Ing. Peter Paško" a "Peter Paško" kvôli tomu dostali rôzny kľúč).
const ACADEMIC_TITLE_RE =
  /\b(ing\s*arch|ing|mgr|judr|mudr|rndr|phdr|doc|prof|bc|phd|csc|dr)\b\.?/gi;

function normalizePersonNameLoose(fullName: string): string {
  return fullName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/,/g, ' ')
    .replace(ACADEMIC_TITLE_RE, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function groupPeople(people: { edge: GraphEdge; person: GraphNode }[]): {
  current: PersonRoles[];
  past: PersonRoles[];
} {
  const byPerson = new Map<string, PersonRoles & { hasCurrent: boolean }>();
  for (const { edge, person } of people) {
    const key = normalizePersonNameLoose(person.label);
    const entry = byPerson.get(key) ?? {
      key,
      label: person.label,
      identityConfirmed: person.identityConfirmed,
      roles: [],
      sources: [],
      since: null,
      until: null,
      hasCurrent: false,
    };
    if (person.label.length > entry.label.length) entry.label = person.label;
    if (person.identityConfirmed) entry.identityConfirmed = true;
    const role = edge.roleLabel ?? 'Neuvedená rola';
    if (!entry.roles.includes(role)) entry.roles.push(role);
    if (edge.source && !entry.sources.includes(edge.source)) entry.sources.push(edge.source);
    if (edge.validFrom && (!entry.since || edge.validFrom < entry.since))
      entry.since = edge.validFrom;
    if (!edge.validTo) entry.hasCurrent = true;
    else if (!entry.until || edge.validTo > entry.until) entry.until = edge.validTo;
    byPerson.set(key, entry);
  }
  const all = [...byPerson.values()];
  // Aktuálni najprv podľa dĺžky väzby (najdlhšie slúžiaci hore), bývalí podľa
  // toho, kedy odišli (naposledy odídení hore).
  return {
    current: all
      .filter((p) => p.hasCurrent)
      .map(({ hasCurrent: _h, ...p }) => ({ ...p, until: null }))
      .sort((a, b) => (a.since ?? '').localeCompare(b.since ?? '')),
    past: all
      .filter((p) => !p.hasCurrent)
      .map(({ hasCurrent: _h, ...p }) => p)
      .sort((a, b) => (b.until ?? '').localeCompare(a.until ?? '')),
  };
}

function PersonRow({ p }: { p: PersonRoles }) {
  // "?" (nepotvrdená identita) sa zámerne nezobrazuje tu — v tejto tabuľke sa
  // žiadne prepájanie naprieč firmami nedeje, takže by len varovalo pred
  // problémom, ktorý sa v tomto kontexte vôbec nerieši (2026-07-25, user
  // feedback). Reálny dôsledok (graf sa cez takú osobu ďalej nerozvetvuje) je
  // len v NetworkGraph, tam "?" ostáva.
  return (
    <tr>
      <td>{p.label}</td>
      <td>{p.roles.join(', ')}</td>
      <td className="amt num">{formatSkDate(p.since)}</td>
      <td className="amt num">{p.until ? formatSkDate(p.until) : 'trvá'}</td>
      <td className="src-cell">{p.sources.map((x) => SOURCE_LABELS[x] ?? x).join(' · ')}</td>
    </tr>
  );
}

function PeopleRoster({ people }: { people: { edge: GraphEdge; person: GraphNode }[] }) {
  const { current, past } = groupPeople(people);
  return (
    <>
      <div className="fin-panel">
        <div className="overflow-x">
          <table className="dense-table people-table">
            <thead>
              <tr>
                <th>Osoba</th>
                <th>Vzťah k firme</th>
                <th className="amt">Od</th>
                <th className="amt">Do</th>
                <th>Zdroj</th>
              </tr>
            </thead>
            <tbody>
              {current.map((p) => (
                <PersonRow key={p.key} p={p} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {past.length > 0 && (
        <details className="past-people">
          <summary>
            Bývalé väzby ({past.length}{' '}
            {past.length === 1 ? 'osoba' : past.length < 5 ? 'osoby' : 'osôb'})
          </summary>
          <div className="fin-panel" style={{ marginTop: 10 }}>
            <div className="overflow-x">
              <table className="dense-table people-table">
                <thead>
                  <tr>
                    <th>Osoba</th>
                    <th>Vzťah k firme</th>
                    <th className="amt">Od</th>
                    <th className="amt">Do</th>
                    <th>Zdroj</th>
                  </tr>
                </thead>
                <tbody>
                  {past.map((p) => (
                    <PersonRow key={p.key} p={p} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </details>
      )}
    </>
  );
}

// Rizikové skóre ako oblúkový gauge (2026-07-20, na žiadosť usera podľa vzoru
// kreditného skóre). Dôležitý rozdiel oproti kreditnému skóre: naša škála je
// OBRÁTENÁ — 0 je najlepšie, 100 najhoršie. Preto ide zelená vľavo a červená
// vpravo, nie naopak.
//
// Oblúk sa nekreslí ako plynulý gradient, ale ako tri farebné segmenty na
// hraniciach 20 a 50 — to sú skutočné prahy z riskBand() v styles.ts. Gradient
// by predstieral spojitú škálu, ktorú produkt nemá, a nepovedal by, kde presne
// firma preklápa z jedného pásma do druhého.
const GAUGE_BANDS: { from: number; to: number; band: 'low' | 'mid' | 'high' }[] = [
  { from: 0, to: 20, band: 'low' },
  { from: 20, to: 50, band: 'mid' },
  { from: 50, to: 100, band: 'high' },
];
const GAUGE_START = -135; // dolu vľavo
const GAUGE_SWEEP = 270;

function gaugePoint(cx: number, cy: number, r: number, value: number) {
  const a = ((GAUGE_START + (value / 100) * GAUGE_SWEEP) * Math.PI) / 180;
  return { x: cx + r * Math.sin(a), y: cy - r * Math.cos(a) };
}
function gaugeArc(cx: number, cy: number, r: number, from: number, to: number): string {
  const p1 = gaugePoint(cx, cy, r, from);
  const p2 = gaugePoint(cx, cy, r, to);
  const large = ((to - from) / 100) * GAUGE_SWEEP > 180 ? 1 : 0;
  return `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
}

function RiskGauge({ score, signalCount }: { score: number; signalCount: number }) {
  const clamped = Math.max(0, Math.min(100, score));
  const band = riskBand(score);
  const cx = 110;
  const cy = 108;
  const r = 82;
  const marker = gaugePoint(cx, cy, r, clamped);
  return (
    <div className="risk-gauge">
      <svg
        viewBox="0 0 220 200"
        role="img"
        aria-label={`Rizikové skóre ${score} zo 100 — ${riskWord(score)}`}
      >
        {/* Podklad oblúka — aby bola vidieť celá škála, nielen prejdená časť. */}
        <path
          d={gaugeArc(cx, cy, r, 0, 100)}
          fill="none"
          stroke="var(--bg-soft)"
          strokeWidth="15"
          strokeLinecap="round"
        />
        {GAUGE_BANDS.map((b) => (
          <path
            key={b.band}
            d={gaugeArc(cx, cy, r, b.from, b.to)}
            fill="none"
            stroke={`var(--risk-${b.band})`}
            strokeWidth="15"
            strokeLinecap="butt"
            opacity={b.band === band ? 1 : 0.42}
          />
        ))}
        {/* Značka polohy — biely golier ju drží čitateľnú na ktorejkoľvek farbe. */}
        <circle cx={marker.x} cy={marker.y} r="9.5" fill="var(--bg-raised)" />
        <circle cx={marker.x} cy={marker.y} r="5.5" fill={`var(--risk-${band})`} />

        {/* Popisky prahov — hovoria, kde sa pásma prepínajú. */}
        {[0, 20, 50, 100].map((v) => {
          const p = gaugePoint(cx, cy, r + 20, v);
          return (
            <text
              key={v}
              x={p.x}
              y={p.y + 4}
              textAnchor="middle"
              style={{ fontSize: 11, fontFamily: 'var(--mono)', fill: 'var(--slate-light)' }}
            >
              {v}
            </text>
          );
        })}

        <text
          x={cx}
          y={cy + 6}
          textAnchor="middle"
          style={{
            fontSize: 40,
            fontFamily: 'var(--mono)',
            fontWeight: 700,
            fill: `var(--risk-${band})`,
          }}
        >
          {score}
        </text>
        <text
          x={cx}
          y={cy + 28}
          textAnchor="middle"
          style={{ fontSize: 13, fontWeight: 700, fill: 'var(--ink)' }}
        >
          {riskWord(score)}
        </text>
        <text
          x={cx}
          y={cy + 46}
          textAnchor="middle"
          style={{ fontSize: 11, fill: 'var(--slate-light)' }}
        >
          {signalCount === 0
            ? 'bez rizikových signálov'
            : `${signalCount} ${signalCount === 1 ? 'signál' : signalCount < 5 ? 'signály' : 'signálov'}`}
        </text>
      </svg>
    </div>
  );
}

// Zloženie súvahy (2026-07-24) — predtým sa Majetok/Zdroje krytia dali vyčítať
// len z riadkov `fin-table` vedľa seba, bez zloženia (obežný vs. neobežný,
// vlastné imanie vs. záväzky). Dva `.seg-bar` panely (rovnaký vzor ako
// "zloženie risk skóre") + rozpis podpoložiek, ktoré RuzTemplateClient odteraz
// vie dekódovať (nonCurrentAssets/intangibleAssets/tangibleAssets/
// longTermFinancialAssets/shareCapital). Len najnovší rok — viacročné
// porovnanie už rieši `fin-table` vyššie, netreba duplikovať tabmi (žiadne
// taby v tomto dokumente, viď 2026-07-20 redizajn).
interface DonutPart {
  value: number | null | undefined;
  className: string;
}
interface DonutSeg {
  className: string;
  dasharray: string;
  dashoffset: number;
}
// Koncentrický donut (mockup "Vidíš skrz") — segmenty počítané dynamicky z
// reálnych dát (na rozdiel od mockupu, ktorý mal hodnoty natvrdo zapísané).
function donutSegments(radius: number, total: number, parts: DonutPart[]): DonutSeg[] {
  const circumference = 2 * Math.PI * radius;
  let cursor = 0;
  const out: DonutSeg[] = [];
  for (const p of parts) {
    if (p.value == null || p.value <= 0 || total <= 0) continue;
    const len = (p.value / total) * circumference;
    out.push({
      className: p.className,
      dasharray: `${len.toFixed(2)} ${Math.max(circumference - len, 0).toFixed(2)}`,
      dashoffset: -cursor,
    });
    cursor += len;
  }
  return out;
}

function BalanceComposition({ year }: { year: FinancialSummaryYear }) {
  const total = year.totalAssets;
  if (total == null || total <= 0) return null;

  const current = year.currentAssets;
  const nonCurrent = year.nonCurrentAssets ?? (current != null ? total - current : null);
  const equity = year.equity;
  const shortTermDebt = year.shortTermLiabilities;
  const otherDebt =
    equity != null && shortTermDebt != null ? Math.max(total - equity - shortTermDebt, 0) : null;

  const outerSegs = donutSegments(42, total, [
    { value: equity, className: 'equity' },
    { value: shortTermDebt, className: 'stl' },
    { value: otherDebt, className: 'oth' },
  ]);
  const innerSegs = donutSegments(28, total, [
    { value: year.financialAssets, className: 'fin' },
    { value: year.shortTermReceivables, className: 'rec' },
    { value: year.inventory, className: 'inv' },
    { value: nonCurrent, className: 'fixed' },
  ]);

  return (
    <div className="bal-composition">
      <div className="bal-donut-combo">
        <svg viewBox="0 0 100 100" role="img" aria-label={`Zloženie súvahy ${year.period}`}>
          <circle className="bs-track-o" cx="50" cy="50" r="42" />
          {outerSegs.map((s) => (
            <circle
              key={`o-${s.className}`}
              className={`bs-seg ${s.className}`}
              cx="50"
              cy="50"
              r="42"
              strokeDasharray={s.dasharray}
              strokeDashoffset={s.dashoffset}
              transform="rotate(-90 50 50)"
            />
          ))}
          <circle className="bs-track-i" cx="50" cy="50" r="28" />
          {innerSegs.map((s) => (
            <circle
              key={`i-${s.className}`}
              className={`bs-seg ${s.className}`}
              cx="50"
              cy="50"
              r="28"
              strokeDasharray={s.dasharray}
              strokeDashoffset={s.dashoffset}
              transform="rotate(-90 50 50)"
            />
          ))}
        </svg>
        <div className="bal-donut-label">
          <b>{formatEurCompact(total)}</b>
          <span>spolu {year.period}</span>
        </div>
      </div>

      <div className="bal-rows-combo">
        <div className="bal-rows-col">
          <div className="bal-rows-h">
            <span className="ring-dot outer" />
            Pasíva (vonkajší kruh)
          </div>
          {equity != null && (
            <div className="bal-row">
              <span className="sw equity" />
              <span className="bal-row-label">Vlastné imanie</span>
              <span className="v">{formatEurCompact(equity)}</span>
            </div>
          )}
          {year.shareCapital != null && (
            <div className="bal-row sub">
              <span className="bal-row-label">— základné imanie</span>
              <span className="v">{formatEurCompact(year.shareCapital)}</span>
            </div>
          )}
          {year.retainedEarnings != null && (
            <div className="bal-row sub">
              <span className="bal-row-label">— nerozdelený zisk minulých rokov</span>
              <span className="v">{formatEurCompact(year.retainedEarnings)}</span>
            </div>
          )}
          {shortTermDebt != null && (
            <div className="bal-row">
              <span className="sw stl" />
              <span className="bal-row-label">Krátkodobé záväzky</span>
              <span className="v">{formatEurCompact(shortTermDebt)}</span>
            </div>
          )}
          {year.tradePayables != null && (
            <div className="bal-row sub">
              <span className="bal-row-label">— z obchodného styku</span>
              <span className="v">{formatEurCompact(year.tradePayables)}</span>
            </div>
          )}
          {otherDebt != null && otherDebt > 0 && (
            <div className="bal-row">
              <span className="sw oth" />
              <span className="bal-row-label">Ostatné záväzky</span>
              <span className="v">{formatEurCompact(otherDebt)}</span>
            </div>
          )}
          {year.bankLoansTotal != null && year.bankLoansTotal > 0 && (
            <div className="bal-row sub">
              <span className="bal-row-label">— z toho bankové úvery a výpomoci</span>
              <span className="v">{formatEurCompact(year.bankLoansTotal)}</span>
            </div>
          )}
        </div>

        <div className="bal-rows-col">
          <div className="bal-rows-h">
            <span className="ring-dot inner" />
            Aktíva (vnútorný kruh)
          </div>
          {year.financialAssets != null && (
            <div className="bal-row">
              <span className="sw fin" />
              <span className="bal-row-label">Finančné účty</span>
              <span className="v">{formatEurCompact(year.financialAssets)}</span>
            </div>
          )}
          {year.shortTermReceivables != null && (
            <div className="bal-row">
              <span className="sw rec" />
              <span className="bal-row-label">Pohľadávky</span>
              <span className="v">{formatEurCompact(year.shortTermReceivables)}</span>
            </div>
          )}
          {year.inventory != null && (
            <div className="bal-row">
              <span className="sw inv" />
              <span className="bal-row-label">Zásoby</span>
              <span className="v">{formatEurCompact(year.inventory)}</span>
            </div>
          )}
          {nonCurrent != null && (
            <div className="bal-row">
              <span className="sw fixed" />
              <span className="bal-row-label">Neobežný majetok</span>
              <span className="v">{formatEurCompact(nonCurrent)}</span>
            </div>
          )}
          {year.tangibleAssets != null && (
            <div className="bal-row sub">
              <span className="bal-row-label">— dlhodobý hmotný</span>
              <span className="v">{formatEurCompact(year.tangibleAssets)}</span>
            </div>
          )}
          {year.intangibleAssets != null && (
            <div className="bal-row sub">
              <span className="bal-row-label">— dlhodobý nehmotný</span>
              <span className="v">{formatEurCompact(year.intangibleAssets)}</span>
            </div>
          )}
          {year.longTermFinancialAssets != null && (
            <div className="bal-row sub">
              <span className="bal-row-label">— dlhodobý finančný</span>
              <span className="v">{formatEurCompact(year.longTermFinancialAssets)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Altman Z ako zónová škála (2026-07-20). Predtým to bol jeden hustý riadok
// textu s pilulkou — číslo 13.94 nič nehovorilo, kým človek nevedel, kde ležia
// hranice. Prahy sú tie, ktoré reálne používa altman-z-score.service.ts
// (distress < 1.23, sivá 1.23–2.9, bezpečná > 2.9) — klasický Z' pre súkromné
// firmy (Altman 1983), zosúladené s FinStatom 2026-07-20 (predtým sme počítali
// iný variant, Z", ktorý s FinStatom systematicky nesedel).
const ALTMAN_MAX = 6;
const ALTMAN_T1 = 1.23;
const ALTMAN_T2 = 2.9;

function AltmanScale({ altman }: { altman: AltmanZScore }) {
  const band = altmanBand(altman.zone);
  // Skóre môže byť ďaleko za škálou (ESET má 13.94) aj hlboko záporné —
  // značka sa priklincuje na okraj a text to prizná, radšej než ťahať škálu.
  const offScale = altman.score > ALTMAN_MAX || altman.score < 0;
  const pos = (Math.max(0, Math.min(ALTMAN_MAX, altman.score)) / ALTMAN_MAX) * 100;
  return (
    <div className="fin-panel altman-panel">
      <div className="altman-head">
        <div>
          <span className="altman-title">Altman Z</span>
          <span className="altman-score num" style={{ color: `var(--risk-${band})` }}>
            {altman.score}
          </span>
          <span className={`pill ${band}`}>{altmanZoneWord(altman.zone)}</span>
        </div>
        <span className="hint">obdobie {altman.period}</span>
      </div>

      {/* Značka je súrodenec pásma, nie jeho dieťa — pásma majú overflow:hidden
          kvôli zaobleniu, ktoré by značku na okraji (a jej "mimo škály" šípku)
          orezalo. */}
      <div className="altman-track" aria-hidden="true">
        <div className="altman-zones">
          <span
            className="altman-zone distress"
            style={{ width: `${(ALTMAN_T1 / ALTMAN_MAX) * 100}%` }}
          >
            Riziková
          </span>
          <span
            className="altman-zone grey"
            style={{ width: `${((ALTMAN_T2 - ALTMAN_T1) / ALTMAN_MAX) * 100}%` }}
          >
            Sivá
          </span>
          <span
            className="altman-zone safe"
            style={{ width: `${((ALTMAN_MAX - ALTMAN_T2) / ALTMAN_MAX) * 100}%` }}
          >
            Bezpečná
          </span>
        </div>
        <span
          className={`altman-marker ${band}${offScale ? ' off' : ''}`}
          style={{ left: `${pos}%` }}
        />
      </div>
      <div className="altman-ticks num" aria-hidden="true">
        <span style={{ left: 0 }}>0</span>
        <span style={{ left: `${(ALTMAN_T1 / ALTMAN_MAX) * 100}%` }}>1,23</span>
        <span style={{ left: `${(ALTMAN_T2 / ALTMAN_MAX) * 100}%` }}>2,90</span>
        <span style={{ left: '100%' }}>6,00</span>
      </div>

      <p className="altman-note">
        {offScale && altman.score > ALTMAN_MAX
          ? `Skóre ${altman.score} je hlboko nad hranicou bezpečnej zóny — značka stojí na okraji škály. `
          : offScale
            ? `Skóre ${altman.score} je pod spodným okrajom škály. `
            : ''}
        Nezávislý akademický ukazovateľ rizika bankrotu pre neobchodované firmy. Nevstupuje do
        rizikového skóre priamo — body pridáva iba riziková zóna.
      </p>
    </div>
  );
}

function altmanBand(zone: 'safe' | 'grey' | 'distress'): 'low' | 'mid' | 'high' {
  if (zone === 'safe') return 'low';
  if (zone === 'grey') return 'mid';
  return 'high';
}
function altmanZoneWord(zone: 'safe' | 'grey' | 'distress'): string {
  if (zone === 'safe') return 'Bezpečná zóna';
  if (zone === 'grey') return 'Sivá zóna';
  return 'Riziková zóna';
}

const INSOLVENCY_LABELS: Record<string, string> = {
  konkurz_restrukturalizacia: 'Konkurz/reštrukturalizácia',
  konkurz_vyrovnanie: 'Vyrovnanie',
  likvidacia: 'Likvidácia',
  znizenie_imania: 'Zníženie základného imania',
};

const REPLIK_TYP_LABELS: Record<string, string> = {
  KONKURZ: 'Konkurz',
  MALYKONKURZ: 'Malý konkurz',
  RESTRUKTURALIZACIA: 'Reštrukturalizácia',
  LIKVIDACIA: 'Likvidácia',
  INEKONANIA: 'Iné konanie',
  'ODDLZENIE-KONKURZ': 'Oddlženie (konkurz)',
  'ODDLZENIE-SPLATKOVYKALENDAR': 'Oddlženie (splátkový kalendár)',
  VPR: 'Verejná preventívna reštrukturalizácia',
};

const RPVS_EVENT_LABELS: Record<string, string> = {
  vymaz_dobrovolny: 'Výmaz z RPVS (dobrovoľný)',
  vymaz_ex_offo: 'Výmaz z RPVS (ex offo)',
  pokuta: 'Sankcia (RPVS)',
  kvalifikovany_podnet: 'Kvalifikovaný podnet',
  verejny_funkcionar: 'Verejný funkcionár napojený na firmu',
  kuv_neoverene: 'Neoverená identita konečného užívateľa výhod',
};

// Naratívna veta o medziročnej zmene (Finstat-štýl "znížila zisk o X% na Y€"),
// postavená z už existujúcich dát (financialSummary), žiadny nový fetch.
function buildNarrative(summary: FinancialSummaryYear[] | undefined): string | null {
  if (!summary || summary.length < 2) return null;
  const [latest, previous] = summary;
  if (!latest || !previous) return null;
  const revenueDelta = delta(latest.revenue, previous.revenue);
  const incomeDelta = delta(latest.netIncome, previous.netIncome);
  if (!revenueDelta && !incomeDelta) return null;

  const parts: string[] = [];
  if (incomeDelta) {
    parts.push(
      `${incomeDelta.dir === 'up' ? 'zvýšila' : 'znížila'} výsledok hospodárenia o ${incomeDelta.pct} % na ${formatEur(latest.netIncome)}`,
    );
  }
  if (revenueDelta) {
    parts.push(
      `tržby ${revenueDelta.dir === 'up' ? 'jej vzrástli' : 'jej klesli'} o ${revenueDelta.pct} % na ${formatEur(latest.revenue)}`,
    );
  }
  return `Firma v roku ${latest.period} ${parts.join(', ')}.`;
}

function ratioPct(numerator?: number | null, denominator?: number | null): string {
  if (numerator == null || !denominator) return '—';
  return `${((numerator / denominator) * 100).toFixed(1)} %`;
}

function sumAmounts(rows: { amount: string }[]): number {
  return rows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
}

// Zistenia — očíslované vety zoradené podľa závažnosti; prvá sekcia dokumentu
// a jediná, ktorú si prečíta aj laik. Rizikové vety idú priamo z riskScore.signals
// (sú už vecne overené a obodované), posledná zhrnie zdravé jadro z financií.
function buildFindings(
  riskScore: RiskScore | undefined,
  financialSummary: FinancialSummaryYear[] | undefined,
  proceedingCount: number,
): string[] {
  const findings: string[] = [
    ...(riskScore?.signals ?? [])
      .slice()
      .sort((a, b) => b.points - a.points)
      .slice(0, 4)
      .map((s) => (s.detail ? `${s.label} — ${s.detail}` : s.label)),
  ];
  const latest = financialSummary?.[0];
  if (latest) {
    const revDelta = delta(latest.revenue, financialSummary?.[1]?.revenue);
    const coreParts = [
      `tržby ${formatEur(latest.revenue)}${revDelta ? ` (${revDelta.dir === 'up' ? '+' : '−'}${revDelta.pct} %)` : ''}`,
      latest.netIncome != null
        ? latest.netIncome < 0
          ? `strata ${formatEur(Math.abs(latest.netIncome))}`
          : `zisk ${formatEur(latest.netIncome)}`
        : null,
      proceedingCount === 0 ? 'bez insolvenčného konania' : null,
    ].filter(Boolean);
    findings.push(
      `${findings.length > 0 ? 'Inak' : 'Celkovo'} za rok ${latest.period}: ${coreParts.join(', ')}.`,
    );
    // Staroba dát je zistenie ako každé iné — doteraz nebola na stránke vôbec.
    const staleYears = new Date().getFullYear() - latest.period;
    if (staleYears >= 2) {
      findings.push(
        `Hodnotenie sa opiera o ${staleYears} ${staleYears < 5 ? 'roky' : 'rokov'} staré výkazy — novšia závierka nebola v RÚZ zverejnená. Pred obchodným rozhodnutím si vyžiadaj aktuálne výkazy priamo od firmy.`,
      );
    }
  }
  return findings;
}

// Prázdny stav sekcie (2026-07-25) — predtým priama URL na sekciu, ktorú táto
// konkrétna firma nemá vyplnenú (napr. /firma/:ico/historia bez histórie
// zápisov), vykreslila len hlavičku a pätičku bez akéhokoľvek vysvetlenia
// (audit zistenie, verzia s prázdnym `main.innerHTML`). Nahrádza to tento
// istý vzor, aký appka už používa v `masthead-figures-empty` pre chýbajúcu
// závierku — vysvetlenie namiesto ticha.
function EmptySection({ label, message }: { label: string; message: string }) {
  return (
    <section className="doc-section">
      <div className="section-title">
        <h2>{label}</h2>
      </div>
      <p className="doc-lede">{message}</p>
    </section>
  );
}

// Registrový výpis — zdieľaný medzi hlavičkou (štvorec vedľa Záväzkov voči
// štátu, viditeľný na každom tabe) a tabom Základné údaje (2026-07-25, user
// chcel v hlavičke celý výpis, nie skrátený výber — jedna definícia namiesto
// dvoch kópií tej istej dl).
function RegistryFacts({ data }: { data: CompanyDetail }) {
  return (
    <dl className="profile-facts">
      <dt>IČO</dt>
      <dd className="num">{data.ico}</dd>
      <dt>Názov</dt>
      <dd>{data.name ?? '—'}</dd>
      {data.dic && (
        <>
          <dt>DIČ</dt>
          <dd className="num">{data.dic}</dd>
        </>
      )}
      <dt>Platiteľ DPH</dt>
      <dd>
        {data.hasDphRegistration ? (
          <>
            Áno{data.icDph ? ` — ${data.icDph}` : ''}
            {data.dphRegistrationType && ` (${data.dphRegistrationType})`}
            {data.dphRegisteredFrom && ` od ${formatSkDate(data.dphRegisteredFrom)}`}
          </>
        ) : (
          'Nie'
        )}
        {data.hasDphCancellationReason && (
          <div className="hint" style={{ display: 'block' }}>
            V minulosti nastal dôvod na zrušenie registrácie pre DPH
            {data.dphCancellationYear && ` (${data.dphCancellationYear})`} — Finančná správa SR
          </div>
        )}
      </dd>
      <dt>Sídlo</dt>
      <dd>
        {data.address
          ? [data.address.street, data.address.city, data.address.zip]
              .filter(Boolean)
              .join(', ')
          : '—'}
      </dd>
      <dt>Hlavná činnosť (NACE)</dt>
      <dd>
        {data.naceName ?? '—'}
        {data.naceCode != null && (
          <span className="hint" style={{ display: 'block' }}>
            Kód {data.naceCode}
          </span>
        )}
      </dd>
      <dt>Právna forma</dt>
      <dd>{data.legalFormName ?? '—'}</dd>
      <dt>Stav</dt>
      <dd>{data.terminatedAt ? <>Zaniknutá {formatSkDate(data.terminatedAt)}</> : 'Aktívna'}</dd>
      {data.taxReliabilityIndex && (
        <>
          <dt>Index daňovej spoľahlivosti</dt>
          <dd>
            {data.taxReliabilityIndex}
            <span className="hint" style={{ display: 'block' }}>
              Finančná správa SR
            </span>
          </dd>
        </>
      )}
      <dt>So zápisom v RPVS</dt>
      <dd>{data.hasRpvsRegistration ? 'Áno' : 'Nie'}</dd>
      <dt>Zahraničný spoločník/akcionár</dt>
      <dd>
        {data.hasForeignStakeholder ? 'Áno' : 'Nie'}
        <span className="hint" style={{ display: 'block' }}>
          Odvodené z adresy spoločníkov v RPO, nie oficiálna kategorizácia
        </span>
      </dd>
      <dt>Dáta aktualizované</dt>
      <dd>{formatSkDate(data.registrySyncedAt)}</dd>
    </dl>
  );
}

// Bunka pásu kľúčových čísel v hlavičke. Pás zobrazuje skrátený zápis
// ("564,5 mil. €"), presnú hodnotu nesie `title`.
// Bunka stavovej mriežky "Záväzky voči štátu". Stav je zakódovaný farbou aj
// slovom, nie len farbou — nahrádza štyri rovnaké zelené "NIE" pilulky, ktoré
// pri zdravej firme nenesú takmer žiadnu informáciu.
function StatusCell({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone: 'ok' | 'warn' | 'bad' | 'neutral';
}) {
  return (
    <div className="status-cell">
      <div className="sc-label">{label}</div>
      <div className={`sc-value ${tone}`}>{value}</div>
      {note && <div className="sc-note">{note}</div>}
    </div>
  );
}

// Lepivá bočná lišta (2026-07-20) — nahrádza `.tab-nav`. Okrem navigácie rieši
// aj to, že po odscrollovaní z hlavičky nebolo vidieť, čiu firmu čítaš, a nesie
// stavovú bodku: problémovú sekciu vidno pred doscrollovaním k nej.
interface RailFigureValue {
  value: string;
  delta?: { pct: string; dir: 'up' | 'down' } | null;
  negative?: boolean;
  invertDelta?: boolean;
  tone?: 'good' | 'bad';
}
export interface RailFigures {
  revenue: RailFigureValue;
  netIncome: RailFigureValue;
  equity: RailFigureValue;
  liabilities: RailFigureValue;
  debtRatio: RailFigureValue;
  altmanZ: RailFigureValue;
}

// Kompaktný riadok v úzkom (210px) lepivom paneli — predtým tento súhrn bol
// v tmavom páse pod hlavičkou, ktorý sa pri scrolle stratil (user feedback
// 2026-07-24: "chcem to mať v sidebare vidieť celý čas").
function RailFigure({ label, f }: { label: string; f: RailFigureValue }) {
  // Nulová zmena nie je zmena — bez tohto sa pri nezmenenej položke vykreslí
  // "▲ 0%", čo v úzkom paneli pôsobí ako šum (firmy s jediným rokom výkazov).
  const d = f.delta && Number(f.delta.pct) !== 0 ? f.delta : null;
  const dirGood = d ? (f.invertDelta ? d.dir === 'down' : d.dir === 'up') : false;
  return (
    <div className="rail-figure">
      <div className="rf-label">{label}</div>
      <div className={`rf-value num${f.negative ? ' bad' : ''}${f.tone ? ` ${f.tone}` : ''}`}>
        {f.value}
        {d && (
          <span className={`rf-delta ${dirGood ? 'good' : 'bad'}`}>
            {d.dir === 'up' ? '▲' : '▼'} {d.pct}%
          </span>
        )}
      </div>
    </div>
  );
}

function SectionRail({
  ico,
  sections,
  active,
  isWatching,
  watchLoading,
  onToggleWatch,
  figures,
}: {
  ico: string;
  sections: DocSection[];
  active: string;
  isWatching: boolean;
  watchLoading: boolean;
  onToggleWatch: () => void;
  figures: RailFigures | null;
}) {
  return (
    <nav className="section-rail no-print" aria-label="Obsah stránky">
      {figures && (
        <div className="rail-figures">
          <RailFigure label="Tržby" f={figures.revenue} />
          <RailFigure label="Výsledok hosp." f={figures.netIncome} />
          <RailFigure label="Vlastné imanie" f={figures.equity} />
          <RailFigure label="Záväzky" f={figures.liabilities} />
          <RailFigure label="Zadlženosť" f={figures.debtRatio} />
          <RailFigure label="Altman Z" f={figures.altmanZ} />
        </div>
      )}
      <div className="rail-label">Na tejto stránke</div>
      {sections.map((s) => (
        <Link
          key={s.key}
          to={s.key ? `/firma/${ico}/${s.key}` : `/firma/${ico}`}
          className={active === s.key ? 'on' : undefined}
          aria-current={active === s.key ? 'true' : undefined}
        >
          <span>{s.label}</span>
          {s.status && (
            <span
              className={`rail-dot ${s.status}`}
              title={s.status === 'bad' ? 'vyžaduje pozornosť' : 'bez zistení'}
            />
          )}
        </Link>
      ))}
      <div className="rail-actions">
        <button type="button" onClick={onToggleWatch} disabled={watchLoading}>
          {isWatching ? '✓ Sledujem' : '+ Sledovať firmu'}
        </button>
        <button type="button" className="ghost" onClick={() => window.print()}>
          Stiahnuť PDF
        </button>
      </div>
    </nav>
  );
}

// Benchmark riadok (NACE mediány, 2026-07-18): hodnota firmy ako navy pruh, medián
// odvetvia ako zlatá značka na spoločnej škále. Záporné hodnoty (strata → záporná
// marža/ROE) sa nedajú zmysluplne kresliť ako dĺžka pruhu — pruh je 0 a číslo
// červené; škála = 1.25× väčšej z oboch hodnôt, aby značka nikdy nebola na kraji.
function BenchRow({
  label,
  own,
  median,
}: {
  label: string;
  own: number | null;
  median: number | null;
}) {
  if (own == null || median == null) return null;
  const scale = Math.max(Math.abs(own), Math.abs(median)) * 1.25 || 1;
  const ownPct = Math.max(0, own / scale) * 100;
  const medianPct = Math.max(0, median / scale) * 100;
  return (
    <div className="bench-row">
      <span>{label}</span>
      <span className="num bench-value" style={{ color: own < 0 ? 'var(--risk-high)' : undefined }}>
        {(own * 100).toFixed(1)} %
      </span>
      <span className="bench-bar">
        <span className="own" style={{ width: `${ownPct}%` }} />
        <span className="median" style={{ left: `${medianPct}%` }} />
      </span>
    </div>
  );
}

// Plný riadkový výpis súvahy/výsledovky (Týž. 21, finstat-parita) — na požiadanie,
// viď `showFullStatements` v CompanyPage. Roky sa vyberajú z dropdownu (nie
// zobrazené všetky naraz — desiatky riadkov × N rokov by appku zahltilo),
// prázdne riadky (current aj previous null) sa vynechávajú.
// Ktorá zo 4 hĺbok riadku (0 = súčet za celú stranu, 1 = veľká sekcia
// "A."/"B."/"C.", 2 = "A.I.", 3+ = jednotlivé položky). RÚZ šablóna pri
// pokračujúcich položkách rovnakej úrovne neopakuje celý kód (napr. "2.", "3."
// namiesto "A.II.2.", "A.II.3."), preto sa neznáme pokračovanie berie na
// rovnakej hĺbke ako predchádzajúci riadok s plným kódom — čisto kozmetické
// odsadenie pre čitateľnosť (user feedback 2026-08-03: "áno ukazovať všetko,
// ale takto je to neprehľadné"), na výpočty to nemá vplyv.
function statementRowDepth(oznacenie: string | null, prevDepth: number): number {
  if (!oznacenie) return 0;
  const clean = oznacenie.replace(/\.$/, '').trim();
  if (!clean) return 0;
  const startsWithLetter = /^[A-Za-z]/.test(clean);
  if (!startsWithLetter) return prevDepth;
  return clean.split('.').length;
}

const STATEMENT_TABS = [
  { key: 'aktiva', label: 'Strana aktív' },
  { key: 'pasiva', label: 'Strana pasív' },
  { key: 'vykazZiskovAStrat', label: 'Výkaz ziskov a strát' },
] as const;

function FullStatementsPanel({
  loading,
  years,
}: {
  loading: boolean;
  years: DecodedStatementYear[] | undefined;
}) {
  const [period, setPeriod] = useState<number | null>(null);
  const [tab, setTab] = useState<(typeof STATEMENT_TABS)[number]['key']>('aktiva');
  const selected = years?.find((y) => y.period === (period ?? years[0]?.period));

  if (loading && !years) {
    return (
      <div className="fin-panel" style={{ padding: '18px 22px' }}>
        Načítavam...
      </div>
    );
  }
  if (!years || years.length === 0) {
    return (
      <div className="fin-panel" style={{ padding: '18px 22px' }}>
        Žiadny dekódovateľný výkaz nie je k dispozícii.
      </div>
    );
  }

  return (
    <div className="fin-panel" style={{ padding: '18px 22px' }}>
      <label style={{ display: 'block', marginBottom: '14px' }}>
        <span className="hint">Účtovné obdobie</span>
        <br />
        <select
          value={selected?.period ?? years[0]?.period}
          onChange={(e) => setPeriod(Number(e.target.value))}
        >
          {years.map((y) => (
            <option key={y.period} value={y.period}>
              {y.period}
            </option>
          ))}
        </select>
      </label>
      {/* Záložky namiesto 3 tabuliek pod sebou (user feedback 2026-08-03:
          "škaredo štruktúrované celkovo" — panel mal 2297px, treba scrollovať
          cez celú súvahu aj keď zaujíma len výsledovka). Dáta sa nestrácajú,
          len sa naraz zobrazuje jedna strana výkazu. */}
      <div className="tab-nav no-print" role="tablist">
        {STATEMENT_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? 'active' : undefined}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {selected && (
        <>
          <StatementTable
            title="Strana aktív (Súvaha)"
            rows={selected.aktiva}
            active={tab === 'aktiva'}
          />
          <StatementTable
            title="Strana pasív (Súvaha)"
            rows={selected.pasiva}
            active={tab === 'pasiva'}
          />
          <StatementTable
            title="Výkaz ziskov a strát"
            rows={selected.vykazZiskovAStrat}
            active={tab === 'vykazZiskovAStrat'}
          />
        </>
      )}
    </div>
  );
}

function StatementTable({
  title,
  rows,
  active,
}: {
  title: string;
  rows: DecodedStatementRow[] | null;
  active: boolean;
}) {
  const filled = (rows ?? []).filter((r) => r.current != null || r.previous != null);
  if (filled.length === 0) return null;
  let prevDepth = 1;
  const withDepth = filled.map((r) => {
    prevDepth = statementRowDepth(r.oznacenie, prevDepth);
    return { row: r, depth: prevDepth };
  });
  return (
    <div className={`tab-panel${active ? ' active' : ''}`} style={{ marginTop: '18px' }}>
      <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px' }}>{title}</h3>
      <div className="overflow-x">
        <table className="dense-table statement-table">
          <thead>
            <tr>
              <th>Označenie</th>
              <th>Riadok</th>
              <th className="amt">Bežné obdobie</th>
              <th className="amt">Predchádzajúce obdobie</th>
            </tr>
          </thead>
          <tbody>
            {withDepth.map(({ row: r, depth }, i) => (
              <tr key={i} className={depth <= 1 ? 'statement-row--total' : undefined}>
                <td className="num">{r.oznacenie ?? '—'}</td>
                <td style={{ paddingLeft: `${Math.min(depth, 4) * 14}px` }}>{r.text}</td>
                <td className="amt">{formatEur(r.current)}</td>
                <td className="amt">{formatEur(r.previous)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Predmety podnikania + história názvu/sídla z RPO (registryData.raw) — dáta boli
// zbierané, ale UI z nich dlho extrahovalo len "aktuálny" záznam. Renderuje sa len
// ak sú dáta reálne prítomné (rovnaký vzor ako `companyListings` — nejde o primárnu
// sekciu s garantovanými dátami, takže žiadny "žiadne záznamy" empty-state).
//
// 2026-07-20: pôvodne jeden komponent so štyrmi sekciami. Rozdelený na dva, lebo
// nové poradie dokumentu ich rozdeľuje — predmety podnikania patria k Základným
// údajom (sekcia 09), zmeny názvu/sídla až do archívnej Histórie (10). Spoločníci
// zo štvorice vypadli úplne: presunuli sa do sekcie Vlastníci a štatutári, kde
// dávajú zmysel vedľa štatutárov, namiesto samostatného zoznamu tých istých ľudí.
function rpoEntries(data: CompanyDetail) {
  const raw = data.registryData?.raw;
  const byNewest = (a: { validFrom?: string }, b: { validFrom?: string }) =>
    (b.validFrom ?? '').localeCompare(a.validFrom ?? '');
  return {
    activities: (raw?.activities ?? []).filter((a) => a.economicActivityDescription).sort(byNewest),
    names: (raw?.fullNames ?? []).filter((n) => n.value).sort(byNewest),
    addresses: (raw?.addresses ?? [])
      .filter((a) => a.street || a.municipality?.value)
      .sort(byNewest),
  };
}

// Používa CompanyPage na rozhodnutie, či vôbec pridať položku "História zápisov"
// do bočnej lišty — lišta sa skladá z toho, čo sa reálne vykreslí.
function registryHistoryHasContent(data: CompanyDetail | undefined): boolean {
  if (!data) return false;
  const { names, addresses } = rpoEntries(data);
  return names.length > 1 || addresses.length > 1;
}

function BusinessActivities({ data }: { data: CompanyDetail }) {
  const { activities } = rpoEntries(data);
  if (activities.length === 0) return null;
  return (
    <div className="sub-block">
      <h3>Predmety podnikania</h3>
      <span className="hint">Zdroj: RPO · {activities.length} zapísaných</span>
      <div className="fin-panel" style={{ padding: '18px 22px' }}>
        <table className="dense-table">
          <thead>
            <tr>
              <th>Predmet podnikania</th>
              <th className="amt">Vznik</th>
              <th className="amt">Zánik</th>
            </tr>
          </thead>
          <tbody>
            {activities.map((a, i) => (
              <tr key={i} style={{ opacity: a.validTo ? 0.65 : undefined }}>
                <td>{a.economicActivityDescription}</td>
                <td className="amt num">{formatSkDate(a.validFrom)}</td>
                <td className="amt num">{a.validTo ? formatSkDate(a.validTo) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RegistryChangeHistory({ data }: { data: CompanyDetail }) {
  const { names, addresses } = rpoEntries(data);
  if (names.length <= 1 && addresses.length <= 1) return null;
  return (
    <section id="historia" className="doc-section">
      <div className="section-title">
        <h2>História zápisov</h2>
        <span className="hint">Zdroj: RPO — zmeny názvu a sídla</span>
      </div>

      {names.length > 1 && (
        <div className="sub-block">
          <h3>História názvu</h3>
          <div className="fin-panel" style={{ padding: '18px 22px' }}>
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Názov</th>
                  <th className="amt">Platné od</th>
                  <th className="amt">Platné do</th>
                </tr>
              </thead>
              <tbody>
                {names.map((n, i) => (
                  <tr key={i} style={{ opacity: n.validTo ? 0.65 : undefined }}>
                    <td>{n.value}</td>
                    <td className="amt num">{formatSkDate(n.validFrom)}</td>
                    <td className="amt num">{n.validTo ? formatSkDate(n.validTo) : 'aktuálny'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {addresses.length > 1 && (
        <div className="sub-block">
          <h3>História sídla</h3>
          <div className="fin-panel" style={{ padding: '18px 22px' }}>
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Adresa</th>
                  <th className="amt">Platné od</th>
                  <th className="amt">Platné do</th>
                </tr>
              </thead>
              <tbody>
                {addresses.map((a, i) => (
                  <tr key={i} style={{ opacity: a.validTo ? 0.65 : undefined }}>
                    <td>{addressLabel(a)}</td>
                    <td className="amt num">{formatSkDate(a.validFrom)}</td>
                    <td className="amt num">{a.validTo ? formatSkDate(a.validTo) : 'aktuálna'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

// Skeleton kopíruje nový tvar stránky (lišta + dokument), nie starý `.identity`
// + mriežku dlaždíc — inak pri načítaní preblikne úplne iné rozloženie.
function CompanySkeleton() {
  return (
    <>
      <div className="section-rail no-print" aria-hidden="true">
        {Array.from({ length: 6 }).map((_, i) => (
          <div className="skel" key={i} style={{ height: 12, width: '70%', margin: '10px 18px' }} />
        ))}
      </div>
      <div className="company-doc">
        <div className="skel" style={{ height: 28, width: '38%', marginBottom: 14 }} />
        <div className="fin-panel" style={{ padding: '18px 22px' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              className="skel"
              key={i}
              style={{ height: 14, width: `${90 - i * 12}%`, marginBottom: 12 }}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function formatRatioValue(row: RatioRow): string {
  if (row.unit === 'pct') return formatPct(row.value);
  if (row.unit === 'days') return formatDays(row.value);
  return formatRatio(row.value);
}

// Rozšírené finančné ukazovatele (Corpuls redizajn 2026-07-15, FinStat-inšpirované
// — viď /root/.claude/plans/abstract-finding-unicorn.md). `previous` je voliteľný
// (chýba pri jedinom dostupnom roku výkazov) — bez neho sa trend šípky nezobrazia.
function RatioCategories({
  current,
  previous,
}: {
  current: FinancialSummaryYear;
  previous: FinancialSummaryYear | null;
}) {
  const categories = computeFinancialRatios(current);
  const previousByKey = new Map<string, number | null>();
  if (previous) {
    for (const cat of computeFinancialRatios(previous)) {
      for (const row of cat.rows) previousByKey.set(row.key, row.value);
    }
  }
  return (
    <>
      <div style={{ textAlign: 'right', margin: '0 0 8px' }}>
        <a href="/metodika" className="hint">
          Čo znamenajú tieto ukazovatele →
        </a>
      </div>
      {categories.map((cat: RatioCategory) => (
        <div className="ratio-category" key={cat.key}>
          <div className="ratio-category-title">{cat.label}</div>
          <table className="fact-table">
            <tbody>
              {cat.rows.map((row) => {
                const prevValue = previousByKey.get(row.key);
                const trendDir =
                  row.value != null && prevValue != null && prevValue !== 0
                    ? row.value > prevValue
                      ? 'up'
                      : row.value < prevValue
                        ? 'down'
                        : null
                    : null;
                return (
                  <tr key={row.key}>
                    <td>
                      {row.label}
                      {row.hint && <div className="ratio-hint">{row.hint}</div>}
                    </td>
                    <td className="num">
                      {formatRatioValue(row)}
                      {trendDir && (
                        <span className={`ratio-trend ${trendDir}`}>
                          {trendDir === 'up' ? '▲' : '▼'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}
